import React, { useState, useEffect, useCallback } from 'react';
import * as XLSX from 'xlsx';
import api from '../api';
import { useAuth } from '../context/AuthContext';
import MeetingDetail from './MeetingDetail';

const FLAG_COLOR = { green:'var(--green)', amber:'#FF9900', red:'var(--red)' };
// ── Dashboard helpers (full-page project detail) ────────────────────────────
const initials = n => (n || '?').trim().split(/\s+/).map(x => x[0]).slice(0, 2).join('').toUpperCase();
const AV_COLORS = ['#4f5bd5','#0d9488','#d97706','#db2777','#2f6bff','#16a34a','#8b5cf6','#e5484d'];
const avColor = n => AV_COLORS[((n || '').length + (n || '').charCodeAt(0) || 0) % AV_COLORS.length];
const Avatar = ({ name, size = 26 }) => (
  <span title={name} style={{ width:size, height:size, borderRadius:'50%', background:avColor(name), color:'#fff',
    display:'inline-flex', alignItems:'center', justifyContent:'center', fontSize:size*0.42, fontWeight:600, flexShrink:0 }}>{initials(name)}</span>
);
const PRI_BG = { Low:'var(--bg3)', Medium:'rgba(255,153,0,0.15)', High:'rgba(229,72,77,0.15)', Critical:'rgba(229,72,77,0.22)', Normal:'var(--bg3)' };
const PRI_FG = { Low:'var(--text2)', Medium:'#B26A00', High:'var(--red)', Critical:'var(--red)', Normal:'var(--text2)' };
const taskPct = t => t.progress != null ? t.progress : (['Completed','Closed'].includes(t.status) ? 100 : t.status === 'In Progress' ? 50 : 0);
const isOverdueT = t => t.due_date && String(t.due_date).slice(0,10) < new Date().toISOString().slice(0,10) && !['Completed','Closed'].includes(t.status);
const StatusPill = ({ s }) => {
  const done = ['Completed','Closed'].includes(s), prog = s === 'In Progress';
  const bg = done ? 'rgba(45,168,102,0.15)' : prog ? 'rgba(79,142,247,0.15)' : 'var(--bg3)';
  const fg = done ? 'var(--green)' : prog ? 'var(--accent)' : 'var(--text2)';
  return <span style={{ fontSize:11, fontWeight:600, padding:'3px 10px', borderRadius:20, background:bg, color:fg }}>{s}</span>;
};
const CATS = [['task','Task'], ['information','Information'], ['policy_change','Policy Change']];
const catLabel = c => (CATS.find(x => x[0] === c)?.[1]) || (c ? c : '—');
const cap = s => s ? s.charAt(0).toUpperCase() + s.slice(1) : s;
const isWork = c => c.category === null || c.category === 'task';   // generates tasks
const fmt = d => d ? new Date(d).toLocaleDateString('en-IN', { day:'2-digit', month:'short', year:'2-digit' }) : '—';

function FlagPill({ flag, stale }) {
  return <span style={{ display:'inline-flex', alignItems:'center', gap:5, fontSize:12, fontWeight:600, color:FLAG_COLOR[flag] }}>
    <span style={{ width:9, height:9, borderRadius:'50%', background:FLAG_COLOR[flag] }} />
    {cap(flag)}{stale && <span style={{ color:'var(--text2)', fontWeight:400 }}>· stale</span>}
  </span>;
}

export default function Containers({ mode = 'project', meetingKind = null, title = 'Projects', openId = null, clearArg }) {
  const type = mode;   // 'project' | 'meeting'
  const [rows, setRows] = useState([]);
  const [detailId, setDetailId] = useState(null);
  const [newModal, setNewModal] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [pf, setPf] = useState({ status:'', health:'', priority:'', lead:'', q:'', sort:'updated' });
  const [workload, setWorkload] = useState([]);
  // Meeting kinds are master data now, not two hard-coded pages. `kindFilter`
  // is what the list is narrowed to; the types themselves fill the dropdowns.
  const [types, setTypes] = useState([]);
  const [kindFilter, setKindFilter] = useState(meetingKind || '');
  useEffect(() => {
    if (type !== 'meeting') return;
    api.get('/containers/meeting-types').then(r => setTypes(r.data)).catch(() => setTypes([]));
  }, [type]);
  const typeName = code => types.find(t => t.code === code)?.name || code || '—';
  // Until the first fetch lands, say nothing. Rendering "No meetings yet" while
  // loading reads as though the meetings have gone.
  const [loaded, setLoaded] = useState(false);
  const load = useCallback(() => {
    const params = { type };
    if (type === 'meeting' && kindFilter) params.meeting_kind = kindFilter;
    api.get('/containers', { params })
      .then(r => setRows(r.data)).catch(() => {}).finally(() => setLoaded(true));
    if (type === 'project') api.get('/containers/workload').then(r => setWorkload(r.data)).catch(()=>{});
  }, [type, kindFilter]);
  useEffect(() => { load(); }, [load]);
  // Belt and braces alongside the key in App.js: if this instance is ever reused
  // for a different page, close whatever was open rather than showing a meeting
  // through the project view.
  useEffect(() => { setDetailId(null); }, [type, kindFilter]);
  // Deep-open a specific container passed via navigation (from a task's parent link).
  useEffect(() => { if (openId) { setDetailId(Number(openId)); clearArg && clearArg(); } }, [openId]);

  const noun = type === 'project' ? 'Project' : 'Meeting';

  // Both projects and meetings open their detail as a FULL PAGE, replacing this
  // list (the sidebar stays). A meeting has its own page — the minutes, agenda
  // and acknowledgement tracking have little in common with a project plan.
  if (type === 'project' && detailId)
    return <Detail id={detailId} fullPage onClose={() => { setDetailId(null); load(); }} />;
  if (type === 'meeting' && detailId)
    return <MeetingDetail id={detailId}
      onClose={() => { setDetailId(null); load(); }}
      onNew={() => { setDetailId(null); setNewModal(true); }} />;

  // Portfolio KPIs (whole set) + filtered/sorted tile list — projects only.
  const num = v => Number(v || 0);
  const K = {
    total: rows.length,
    active: rows.filter(r => r.lifecycle === 'Active').length,
    completed: rows.filter(r => r.lifecycle === 'Completed').length,
    onhold: rows.filter(r => r.lifecycle === 'On Hold').length,
    cancelled: rows.filter(r => r.lifecycle === 'Cancelled').length,
    green: rows.filter(r => r.health_flag === 'green').length,
    amber: rows.filter(r => r.health_flag === 'amber').length,
    red: rows.filter(r => r.health_flag === 'red').length,
    avg: rows.length ? Math.round(rows.reduce((s, r) => s + num(r.percent), 0) / rows.length) : 0,
    open: rows.reduce((s, r) => s + num(r.open_tasks), 0),
    overdue: rows.reduce((s, r) => s + num(r.overdue_tasks), 0),
    dueWk: rows.reduce((s, r) => s + num(r.due_week_tasks), 0),
  };
  const owners = [...new Set(rows.map(r => r.owner_name).filter(Boolean))].sort();
  const filtered = rows.filter(r => {
    if (pf.status && r.lifecycle !== pf.status) return false;
    if (pf.health && r.health_flag !== pf.health) return false;
    if (pf.priority && r.priority !== pf.priority) return false;
    if (pf.lead && r.owner_name !== pf.lead) return false;
    if (pf.q && !`${r.title} ${r.category || ''}`.toLowerCase().includes(pf.q.toLowerCase())) return false;
    return true;
  }).sort((a, b) => {
    if (pf.sort === 'progress') return num(b.percent) - num(a.percent);
    if (pf.sort === 'overdue')  return num(b.overdue_tasks) - num(a.overdue_tasks);
    if (pf.sort === 'target')   return (a.target_date || '9999') < (b.target_date || '9999') ? -1 : 1;
    if (pf.sort === 'health')   { const o = { red:0, amber:1, green:2 }; return (o[a.health_flag] ?? 3) - (o[b.health_flag] ?? 3); }
    return 0;
  });
  const tileRows = type === 'project' ? filtered : rows;

  return (
    <div>
      <div className="flex-gap" style={{ justifyContent:'space-between', marginBottom:16, flexWrap:'wrap', gap:8 }}>
        <div>
          <h2 style={{ fontSize:18, fontWeight:600, marginBottom:4 }}>{type === 'project' ? '📁' : '👥'} {title}</h2>
          <p style={{ fontSize:13, color:'var(--text2)' }}>Containers → items → tasks. Roll-ups are automatic; the health flag is set by a human.</p>
        </div>
        <div className="flex-gap" style={{ gap:8 }}>
          {type === 'project' && <button className="btn btn-secondary" onClick={() => setImportOpen(true)}>⬆ Bulk Upload</button>}
          <button className="btn btn-primary" onClick={() => setNewModal(true)}>+ New {noun}</button>
        </div>
      </div>

      {type === 'project' && rows.length > 0 && (<>
        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(120px,1fr))', gap:10, marginBottom:12 }}>
          {[['Total',K.total,'var(--text)'],['Active',K.active,'var(--accent)'],['Completed',K.completed,'var(--green)'],
            ['On hold',K.onhold,'#FF9900'],['Avg progress',K.avg+'%','var(--text)'],['Open tasks',K.open,'var(--text)'],
            ['Overdue',K.overdue,K.overdue>0?'var(--red)':'var(--text)'],['Due this week',K.dueWk,'#FF9900']].map(([l,v,col]) => (
            <div key={l} className="card" style={{ padding:'12px 14px' }}>
              <div style={{ fontSize:12, color:'var(--text2)' }}>{l}</div>
              <div style={{ fontSize:22, fontWeight:700, color:col }}>{v}</div>
            </div>
          ))}
        </div>
        <div className="flex-gap" style={{ gap:6, marginBottom:8, flexWrap:'wrap', alignItems:'center' }}>
          <span style={{ fontSize:12, color:'var(--text2)' }}>Health:</span>
          <span className="badge" style={{ background:'rgba(45,168,102,0.15)', color:'var(--green)' }}>{K.green} green</span>
          <span className="badge" style={{ background:'rgba(255,153,0,0.15)', color:'#FF9900' }}>{K.amber} amber</span>
          <span className="badge" style={{ background:'rgba(229,72,77,0.15)', color:'var(--red)' }}>{K.red} red</span>
        </div>
        <div className="flex-gap" style={{ gap:8, marginBottom:14, flexWrap:'wrap' }}>
          <input placeholder="Search project…" value={pf.q} onChange={e=>setPf(s=>({...s,q:e.target.value}))} style={{ padding:'6px 8px', minWidth:150 }} />
          <select value={pf.status} onChange={e=>setPf(s=>({...s,status:e.target.value}))} style={{ padding:'6px 8px' }}><option value="">All status</option>{['Draft','Planning','Active','On Hold','Completed','Cancelled'].map(x=><option key={x}>{x}</option>)}</select>
          <select value={pf.health} onChange={e=>setPf(s=>({...s,health:e.target.value}))} style={{ padding:'6px 8px' }}><option value="">All health</option>{['green','amber','red'].map(x=><option key={x} value={x}>{cap(x)}</option>)}</select>
          <select value={pf.priority} onChange={e=>setPf(s=>({...s,priority:e.target.value}))} style={{ padding:'6px 8px' }}><option value="">All priority</option>{['Low','Medium','High','Critical'].map(x=><option key={x}>{x}</option>)}</select>
          <select value={pf.lead} onChange={e=>setPf(s=>({...s,lead:e.target.value}))} style={{ padding:'6px 8px' }}><option value="">All leads</option>{owners.map(o=><option key={o}>{o}</option>)}</select>
          <select value={pf.sort} onChange={e=>setPf(s=>({...s,sort:e.target.value}))} style={{ padding:'6px 8px' }} title="Sort by"><option value="updated">Recent</option><option value="progress">Progress</option><option value="overdue">Overdue</option><option value="target">Target date</option><option value="health">Health</option></select>
          {(pf.q||pf.status||pf.health||pf.priority||pf.lead) && <button className="btn btn-secondary btn-sm" onClick={()=>setPf(s=>({ status:'',health:'',priority:'',lead:'',q:'',sort:s.sort }))}>Clear</button>}
        </div>
      </>)}

      {/* Meetings: filter by type, now that all kinds live in one list. */}
      {type === 'meeting' && types.length > 1 && (
        <div className="flex-gap" style={{ gap:8, marginBottom:14, flexWrap:'wrap', alignItems:'center' }}>
          <button onClick={() => setKindFilter('')}
            className={`btn btn-sm ${!kindFilter ? 'btn-primary' : 'btn-secondary'}`}>
            All meetings
          </button>
          {types.map(t => (
            <button key={t.code} onClick={() => setKindFilter(t.code)}
              className={`btn btn-sm ${kindFilter === t.code ? 'btn-primary' : 'btn-secondary'}`}>
              {t.name}
            </button>
          ))}
        </div>
      )}

      {!loaded ? <div className="card"><div className="empty-state"><p>Loading…</p></div></div>
       : !rows.length ? <div className="card"><div className="empty-state"><p>No {type}s yet.</p></div></div>
       : !tileRows.length ? <div className="card"><div className="empty-state"><p>No projects match the filters.</p></div></div> : (
        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(320px, 1fr))', gap:14 }}>
          {tileRows.map(c => (
            <div key={c.id} className="card" style={{ padding:16, cursor:'pointer' }} onClick={() => setDetailId(c.id)}>
              <div className="flex-gap" style={{ justifyContent:'space-between', alignItems:'flex-start', gap:8 }}>
                <div>
                  <div style={{ fontWeight:600 }}>{c.title}</div>
                  {/* Which kind of meeting this is — it was implied by the page
                      before, and one list has to say it. */}
                  {type === 'meeting' && c.meeting_kind && (
                    <div style={{ fontSize:11.5, color:'var(--text2)' }}>{typeName(c.meeting_kind)}</div>
                  )}
                </div>
                <FlagPill flag={c.health_flag} stale={c.flag_stale} />
              </div>
              <div style={{ fontSize:12, color:'var(--text2)' }}>
                {c.owner_name ? `Owner: ${c.owner_name}` : ''}{c.target_date ? ` · due ${fmt(c.target_date)}` : ''}
              </div>
              {type === 'project' && (c.lifecycle || c.priority || num(c.overdue_tasks) > 0) && (
                <div className="flex-gap" style={{ gap:4, flexWrap:'wrap', marginTop:5 }}>
                  {c.lifecycle && <span className="badge badge-normal" style={{ fontSize:10 }}>{c.lifecycle}</span>}
                  {c.priority && <span className="badge badge-normal" style={{ fontSize:10 }}>{c.priority}</span>}
                  {num(c.overdue_tasks) > 0 && <span className="badge badge-faulty" style={{ fontSize:10 }}>⚠ {c.overdue_tasks} overdue</span>}
                </div>
              )}
              <div style={{ marginTop:10 }}>
                <div style={{ height:8, background:'var(--bg3)', borderRadius:4, overflow:'hidden' }}>
                  <div style={{ width:`${c.percent ?? 0}%`, height:'100%', background:'var(--accent)' }} />
                </div>
                <div style={{ fontSize:12, color:'var(--text2)', marginTop:4 }}>
                  {c.percent == null ? 'No task items yet' : `${c.percent}% · ${c.items_complete}/${c.items_total} steps complete`}
                </div>
              </div>
              {/* Live "what's happening now" — projects only */}
              {type === 'project' && (c.cur_step || c.cur_task) && (
                <div style={{ borderTop:'1px solid var(--border)', marginTop:10, paddingTop:8, fontSize:12, lineHeight:1.7 }}>
                  {c.cur_step && <div><span style={{ color:'var(--text2)' }}>Step</span> · {c.cur_step}</div>}
                  {c.cur_task && <div style={{ color:'var(--text2)' }}>→ {c.cur_task}</div>}
                  {c.cur_who && <div style={{ marginTop:3 }}><span className="badge badge-normal" style={{ display:'inline-flex', gap:4, alignItems:'center' }}>👤 {c.cur_who}</span></div>}
                </div>
              )}
              {type === 'project' && !c.cur_step && c.percent != null && c.percent < 100 && (
                <div style={{ fontSize:12, color:'var(--text2)', marginTop:8 }}>No task in progress</div>
              )}
              {c.health_flag !== 'green' && c.health_reason && (
                <div style={{ fontSize:12, color:FLAG_COLOR[c.health_flag], marginTop:8 }}>⚠ {c.health_reason}</div>
              )}
            </div>
          ))}
        </div>
      )}

      {type === 'project' && workload.length > 0 && (
        <div className="card" style={{ padding:0, overflow:'hidden', marginTop:16 }}>
          <div style={{ fontSize:12, fontWeight:600, color:'var(--text2)', padding:'10px 12px' }}>MEMBER WORKLOAD <span style={{ fontWeight:400 }}>— live from project tasks</span></div>
          <div className="table-wrap"><table>
            <thead><tr><th>Member</th><th>Active projects</th><th>Pending</th><th>Overdue</th><th>Due this week</th></tr></thead>
            <tbody>
              {workload.map(w => (
                <tr key={w.id}>
                  <td style={{ fontWeight:500 }}>{w.full_name}</td>
                  <td>{w.active_projects}</td>
                  <td>{w.pending}</td>
                  <td style={{ color: num(w.overdue) > 0 ? 'var(--red)' : 'var(--text2)', fontWeight: num(w.overdue) > 0 ? 600 : 400 }}>{w.overdue}</td>
                  <td style={{ color: num(w.due_week) > 0 ? '#FF9900' : 'var(--text2)' }}>{w.due_week}</td>
                </tr>
              ))}
            </tbody>
          </table></div>
        </div>
      )}

      {importOpen && <ImportProjects onClose={() => setImportOpen(false)} onDone={() => { setImportOpen(false); load(); }} />}
      {newModal && <NewContainer type={type} types={types} defaultKind={kindFilter} onClose={() => setNewModal(false)} onSaved={() => { setNewModal(false); load(); }} />}
      {detailId && type === 'meeting' && <Detail id={detailId} onClose={() => { setDetailId(null); load(); }} />}
    </div>
  );
}

function NewContainer({ type, types = [], defaultKind = '', onClose, onSaved }) {
  // The meeting kind is chosen here now. It defaults to whatever the list is
  // filtered to, which is usually what you mean, and to the first type
  // otherwise.
  const [kind, setKind] = useState(defaultKind || types[0]?.code || '');
  useEffect(() => { if (!kind && types.length) setKind(types[0].code); }, [types, kind]);
  const [f, setF] = useState({ title:'', description:'', start_date:'', target_date:'', timeline:'', priority:'', category:'', desired_outcome:'', lifecycle:'Active' });
  const [busy, setBusy] = useState(false); const [err, setErr] = useState('');
  const [itUsers, setItUsers] = useState([]);
  const [attendees, setAttendees] = useState({});
  const [cats, setCats] = useState([]);
  useEffect(() => { if (type === 'meeting') api.get('/tasks/it-users').then(r => setItUsers(r.data)).catch(()=>{});
    if (type === 'project') api.get('/containers/categories').then(r => setCats(r.data)).catch(()=>{}); }, [type]);
  const save = async () => {
    if (!f.title.trim()) { setErr('Title is required'); return; }
    if (type === 'meeting' && !kind) { setErr('Choose a meeting type'); return; }
    setBusy(true); setErr('');
    try {
      await api.post('/containers', { type, meeting_kind: type === 'meeting' ? kind : null, ...f,
        start_date: f.start_date || null, target_date: f.target_date || null,
        attendees: Object.keys(attendees).filter(k => attendees[k]).map(Number) });
      onSaved();
    } catch (e) { setErr(e.response?.data?.error || 'Failed'); } finally { setBusy(false); }
  };
  return (
    <div className="modal-overlay" onClick={e => e.target===e.currentTarget && false && onClose()}>
      <div className="modal" style={{ maxWidth:480 }}>
        <div className="modal-header"><span className="modal-title">New {type === 'project' ? 'Project' : 'Meeting'}</span>
          <button className="modal-close" onClick={onClose}>×</button></div>
        {type === 'meeting' && (
          <div className="form-group"><label>Meeting type *</label>
            <select value={kind} onChange={e => setKind(e.target.value)}>
              {!types.length && <option value="">No meeting types defined</option>}
              {types.map(t => <option key={t.code} value={t.code}>{t.name}</option>)}
            </select>
            {!types.length && (
              <div style={{ fontSize:11.5, color:'var(--red)', marginTop:4 }}>
                Add one under IT Resources → Meeting Types first.
              </div>
            )}
          </div>
        )}
        <div className="form-group"><label>Title *</label>
          <input value={f.title} onChange={e => setF(s => ({ ...s, title:e.target.value }))} /></div>
        <div className="form-row">
          <div className="form-group"><label>{type === 'meeting' ? 'Meeting Date' : 'Start Date'}</label>
            <input type="date" value={f.start_date} onChange={e => setF(s => ({ ...s, start_date:e.target.value }))} /></div>
          <div className="form-group"><label>Target Date</label>
            <input type="date" value={f.target_date} onChange={e => setF(s => ({ ...s, target_date:e.target.value }))} /></div>
        </div>
        {type === 'project' && (<>
          <div className="form-row">
            <div className="form-group"><label>Status</label>
              <select value={f.lifecycle} onChange={e => setF(s => ({ ...s, lifecycle:e.target.value }))}>
                {['Draft','Planning','Active','On Hold'].map(x => <option key={x}>{x}</option>)}
              </select></div>
            <div className="form-group"><label>Priority</label>
              <select value={f.priority} onChange={e => setF(s => ({ ...s, priority:e.target.value }))}>
                <option value="">—</option>{['Low','Medium','High','Critical'].map(x => <option key={x}>{x}</option>)}
              </select></div>
            <div className="form-group"><label>Category</label>
              <input list="new-proj-cats" value={f.category} onChange={e => setF(s => ({ ...s, category:e.target.value }))} placeholder="e.g. Infrastructure" />
              <datalist id="new-proj-cats">{cats.map(c => <option key={c} value={c} />)}</datalist></div>
          </div>
          <div className="form-group"><label>Timeline <span style={{ fontSize:11, color:'var(--text2)' }}>(free text)</span></label>
            <input value={f.timeline} onChange={e => setF(s => ({ ...s, timeline:e.target.value }))} placeholder="e.g. Aug–Sep 2026" /></div>
          <div className="form-group"><label>Desired outcome</label>
            <textarea rows={2} value={f.desired_outcome} onChange={e => setF(s => ({ ...s, desired_outcome:e.target.value }))} /></div>
        </>)}
        {type === 'meeting' && (
          <div className="form-group"><label>Attendees</label>
            <div style={{ display:'flex', flexWrap:'wrap', gap:8, maxHeight:120, overflowY:'auto', border:'1px solid var(--border)', borderRadius:'var(--radius)', padding:8 }}>
              {itUsers.map(u => (
                <label className="opt" key={u.id} style={{ display:'flex', alignItems:'center', gap:4, fontSize:12 }}>
                  <input type="checkbox" checked={!!attendees[u.id]} onChange={() => setAttendees(a => ({ ...a, [u.id]:!a[u.id] }))} /> {u.full_name}
                </label>
              ))}
            </div>
          </div>
        )}
        <div className="form-group"><label>Description</label>
          <textarea rows={3} value={f.description} onChange={e => setF(s => ({ ...s, description:e.target.value }))} /></div>
        {err && <div className="err-msg">{err}</div>}
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={save} disabled={busy}>{busy?'…':'Create'}</button>
        </div>
      </div>
    </div>
  );
}

function Detail({ id, onClose, fullPage = false }) {
  const [d, setD] = useState(null);
  const [itUsers, setItUsers] = useState([]);
  const [campuses, setCampuses] = useState([]);
  const [flag, setFlag] = useState({ health_flag:'green', health_reason:'' });
  const [tl, setTl] = useState('');   // editable project timeline (free text)
  const [det, setDet] = useState({ lifecycle:'', priority:'', category:'', desired_outcome:'' });
  const [cats, setCats] = useState([]);   // project category suggestions
  const [newPhase, setNewPhase] = useState({ title:'', start_date:'', end_date:'' });   // inline "add phase" form
  const [newItem, setNewItem] = useState({ title:'', description:'', category:'task' });
  const [pointCampus, setPointCampus] = useState({});   // campus_ids for a Task point
  const [pointUsers, setPointUsers] = useState({});     // IT admins for a Policy Change point
  const [carry, setCarry] = useState([]);
  const [fanFor, setFanFor] = useState(null);
  const [addFor, setAddFor] = useState(null);   // project step -> inline "add task" form
  const [tasksFor, setTasksFor] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [wl, setWl] = useState([]);
  const [msg, setMsg] = useState('');
  const [tab, setTab] = useState('overview');   // dashboard tab (full-page project view)
  const [editOpen, setEditOpen] = useState(false);
  const [addTaskOpen, setAddTaskOpen] = useState(false);

  const load = useCallback(() => {
    api.get(`/containers/${id}`).then(r => { setD(r.data); setFlag({ health_flag:r.data.health_flag, health_reason:r.data.health_reason || '' }); setTl(r.data.timeline || '');
      setDet({ lifecycle:r.data.lifecycle||'Active', priority:r.data.priority||'', category:r.data.category||'', desired_outcome:r.data.desired_outcome||'' }); }).catch(()=>{});
    api.get(`/containers/${id}/carry-forward`).then(r => setCarry(r.data)).catch(()=>setCarry([]));
    api.get(`/containers/workload`, { params:{ container_id:id } }).then(r => setWl(r.data)).catch(()=>setWl([]));
  }, [id]);
  useEffect(() => { load();
    api.get('/tasks/it-users').then(r => setItUsers(r.data)).catch(()=>{});
    api.get('/master/campuses').then(r => setCampuses(r.data.filter(c=>c.active))).catch(()=>{});
    api.get('/containers/categories').then(r => setCats(r.data)).catch(()=>{});
  }, [load]);
  const { user } = useAuth();
  if (!d) return null;
  const isMeeting = d.type === 'meeting';
  const isProject = d.type === 'project';
  const PLAN_STATUSES = ['Pending', 'Assigned', 'In Progress', 'Completed', 'Closed'];

  // "What's happening now": earliest step with an open task; prefer In Progress.
  const workItems = (d.items || []).filter(isWork);
  let curStep = null, curTask = null, curWho = null;
  for (const it of workItems) {
    const open = (it.tasks || []).filter(t => !['Completed', 'Closed'].includes(t.status));
    if (open.length) {
      curStep = it.title;
      const ip = open.find(t => t.status === 'In Progress') || open[0];
      curTask = ip.title; curWho = ip.assigned_to_name;
      break;
    }
  }
  const roadmap = workItems.map(it => ({
    title: it.title, start: it.start_date, end: it.end_date,
    done: Number(it.tasks_total) > 0 && Number(it.tasks_complete) === Number(it.tasks_total),
    active: it.title === curStep,
  }));
  const fmtShort = v => v ? new Date(v).toLocaleDateString('en-IN', { day:'2-digit', month:'short' }) : '';
  const fmtRange = (s, e) => (!s && !e) ? '' : `${fmtShort(s) || '?'} – ${fmtShort(e) || '?'}`;
  // A member may set the status of their own task; owner / admin / IT Head, any.
  const canEditTask = t => user && (user.role === 'admin' || user.role === 'it_head'
    || d.owner_user_id === user.id || t.assigned_to === user.id);
  const setTaskStatus = async (t, status) => {
    try { await api.patch(`/containers/tasks/${t.id}/status`, { status }); load(); }
    catch (e) { setMsg(e.response?.data?.error || 'Could not update status'); }
  };
  // Owner / admin / IT Head may edit the plan structure (rename steps, edit &
  // reassign tasks); assignees can still set their own task's status.
  const canEditPlan = user && (user.role === 'admin' || user.role === 'it_head' || d.owner_user_id === user.id);

  // Deleting shows what goes with it first — steps and tasks are removed too,
  // and everything is recoverable from the Recycle Bin.
  const openDelete = async () => {
    let impact = { steps: '?', tasks: '?' };
    try { impact = (await api.get(`/containers/${id}/delete-impact`)).data; } catch {}
    const what = isProject ? 'project' : 'meeting';
    const ok = window.confirm(
      `Delete this ${what}?\n\n"${d.title}"\n\n` +
      `This also removes ${impact.steps} step(s) and ${impact.tasks} task(s).\n\n` +
      `It can be restored from the Recycle Bin (Admin / IT Head).`);
    if (!ok) return;
    try {
      const r = await api.delete(`/containers/${id}`);
      alert(`Deleted. ${r.data.tasks_removed || 0} task(s) moved to the Recycle Bin with it.`);
      onClose();
    } catch (e) { alert(e.response?.data?.error || 'Could not delete'); }
  };
  const renameStep = async (it, title) => {
    if (title.trim() && title.trim() !== it.title) {
      try { await api.put(`/containers/items/${it.id}`, { title: title.trim() }); load(); }
      catch (e) { setMsg('Could not rename step'); }
    }
  };
  const editStep = async (it, patch) => {
    try { await api.put(`/containers/items/${it.id}`, patch); load(); }
    catch (e) { setMsg('Could not update phase'); }
  };
  const editTask = async (t, patch) => {
    try { await api.patch(`/containers/tasks/${t.id}/edit`, patch); load(); }
    catch (e) { setMsg(e.response?.data?.error || 'Could not edit task'); }
  };
  const saveTimeline = async () => {
    try { await api.put(`/containers/${id}`, { timeline: tl }); setMsg('✓ Timeline saved'); setTimeout(()=>setMsg(''),1500); load(); }
    catch (e) { setMsg('Could not save timeline'); }
  };
  const saveDetails = async (force = false) => {
    try {
      await api.put(`/containers/${id}`, { ...det, force });
      setMsg('✓ Saved'); setTimeout(()=>setMsg(''),1500); load();
    } catch (e) {
      if (e.response?.status === 409 && e.response.data?.needs_confirm) {
        if (window.confirm(e.response.data.error)) return saveDetails(true);
      } else setMsg(e.response?.data?.error || 'Could not save');
    }
  };
  const addPhase = async () => {
    if (!newPhase.title.trim()) return;
    try {
      await api.post(`/containers/${id}/items`, { title: newPhase.title.trim(), description: '',
        start_date: newPhase.start_date || null, end_date: newPhase.end_date || null });
      setNewPhase({ title:'', start_date:'', end_date:'' }); load();
    } catch (e) { setMsg(e.response?.data?.error || 'Could not add phase'); }
  };
  // Export the full step -> task table + header fields as .xlsx (existing xlsx dep)
  const exportXlsx = () => {
    const rows = [
      ['Project', d.title],
      ['% complete', d.percent == null ? '—' : d.percent + '%'],
      ['Health flag', (d.health_flag || '').toUpperCase() + (d.health_reason ? ` — ${d.health_reason}` : '') + (d.flag_stale ? ' (stale)' : '')],
      ['Target date', d.target_date ? fmt(d.target_date) : '—'],
      ['Members', d.members.map(m => m.full_name).join(', ') || '—'],
      [],
      ['Step', 'Task', 'Assigned To', 'Status', 'Completed On'],
    ];
    d.items.forEach((it, idx) => {
      const step = it.sequence_no || idx + 1;
      const ts = it.tasks || [];
      if (ts.length) ts.forEach(t => rows.push([step, t.title + (t.campus_name ? ` (${t.campus_name})` : ''), t.assigned_to_name || '—', t.status, t.completed_at ? fmt(t.completed_at) : '—']));
      else rows.push([step, it.title, '—', 'No tasks', '—']);
    });
    const ws = XLSX.utils.aoa_to_sheet(rows);
    const wb = XLSX.utils.book_new(); XLSX.utils.book_append_sheet(wb, ws, 'Project');
    XLSX.writeFile(wb, `${d.title.replace(/[^A-Za-z0-9_-]+/g, '_')}.xlsx`);
  };

  const saveFlag = async () => {
    if (flag.health_flag !== 'green' && !flag.health_reason.trim()) { setMsg('Reason required when not green'); return; }
    try { await api.put(`/containers/${id}`, flag); setMsg('✓ Flag saved'); load(); setTimeout(()=>setMsg(''),1500); }
    catch (e) { setMsg(e.response?.data?.error || 'Failed'); }
  };
  const addItem = async () => {
    if (!newItem.title.trim()) return;
    const campus_ids = Object.keys(pointCampus).filter(k => pointCampus[k]).map(Number);
    const user_ids   = Object.keys(pointUsers).filter(k => pointUsers[k]).map(Number);
    const payload = isMeeting
      ? { ...newItem, campus_ids, user_ids }
      : { title:newItem.title, description:newItem.description };
    if (isMeeting && newItem.category === 'task' && !campus_ids.length) { setMsg('Pick at least one campus for a Task point'); return; }
    if (isMeeting && newItem.category === 'policy_change' && !user_ids.length) { setMsg('Pick the IT admins who must acknowledge'); return; }
    try { await api.post(`/containers/${id}/items`, payload);
      setNewItem({ title:'', description:'', category:'task' }); setPointCampus({}); setPointUsers({}); load(); }
    catch (e) { setMsg(e.response?.data?.error || 'Failed'); }
  };
  const acknowledge = async it => { try { await api.post(`/containers/items/${it.id}/acknowledge`); load(); } catch (e) { setMsg(e.response?.data?.error || 'Failed'); } };
  const closePoint = async (it, closed) => { await api.patch(`/containers/items/${it.id}/close`, { closed }); load(); };
  const delItem = async it => { if (window.confirm('Delete this item and its tasks?')) { await api.delete(`/containers/items/${it.id}`); load(); } };
  const addMember = async uid => { if (uid) { await api.post(`/containers/${id}/members`, { user_id:Number(uid) }); load(); } };
  const delMember = async uid => { await api.delete(`/containers/${id}/members/${uid}`); load(); };
  const openTasks = async it => {
    if (tasksFor === it.id) { setTasksFor(null); return; }
    setTasksFor(it.id); const r = await api.get(`/containers/items/${it.id}/tasks`); setTasks(r.data);
  };
  const delTask = async t => { if (window.confirm(`Delete task ${t.task_id}?`)) { await api.delete(`/tasks/${t.id}`); openTasks({ id:tasksFor }); load(); } };

  // ── Redesigned full-page project dashboard ────────────────────────────────
  if (fullPage && isProject) {
    const workItems = (d.items || []).filter(isWork);
    const allTasks = workItems.flatMap(i => (i.tasks || []).map(t => ({ ...t, phase: i.title })));
    const total = allTasks.length;
    const completed = allTasks.filter(t => ['Completed','Closed'].includes(t.status)).length;
    const inprog = allTasks.filter(t => t.status === 'In Progress').length;
    const pending = allTasks.filter(t => !['Completed','Closed','In Progress'].includes(t.status)).length;
    const overdue = allTasks.filter(isOverdueT).length;
    const today = new Date().toISOString().slice(0,10);
    const wk = new Date(Date.now() + 7*864e5).toISOString().slice(0,10);
    const pctT = total ? Math.round(100*completed/total) : 0;
    const pctOf = v => total ? Math.round(100*v/total) + '%' : '0%';
    const phaseInfo = workItems.map((i, idx) => {
      const tt = Number(i.tasks_total), tc = Number(i.tasks_complete);
      const p = tt > 0 ? Math.round(100*tc/tt) : 0;
      const st = i.phase_status || (tt > 0 && tc === tt ? 'Completed' : tt > 0 ? 'In Progress' : 'Not Started');
      const behind = i.end_date && String(i.end_date).slice(0,10) < today && !(tt > 0 && tc === tt);
      return { ...i, num: i.sequence_no || idx+1, pct: p, st, behind };
    });
    const upcoming = allTasks.filter(t => t.due_date && !['Completed','Closed'].includes(t.status))
      .sort((a,b) => String(a.due_date).localeCompare(String(b.due_date))).slice(0,5);
    const recent = [...allTasks].sort((a,b) => String(b.updated_at||'').localeCompare(String(a.updated_at||''))).slice(0,6);
    const dueWeekTasks = allTasks.filter(t => t.due_date && !['Completed','Closed'].includes(t.status) && String(t.due_date).slice(0,10) >= today && String(t.due_date).slice(0,10) <= wk);
    const phasesBehind = phaseInfo.filter(p => p.behind);
    const phasesDone = phaseInfo.filter(p => p.st === 'Completed');

    const StatCard = ({ icon, label, value, sub, color }) => (
      <div className="card" style={{ padding:'14px 16px', display:'flex', alignItems:'center', gap:12 }}>
        <span style={{ width:40, height:40, borderRadius:10, background:(color||'var(--accent)')+'1a', color:color||'var(--accent)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:18, flexShrink:0 }}>{icon}</span>
        <div style={{ flex:1, minWidth:0 }}><div style={{ fontSize:12, color:'var(--text2)' }}>{label}</div>
          <div style={{ fontSize:22, fontWeight:700, lineHeight:1.1 }}>{value}</div></div>
        {sub && <span style={{ fontSize:13, fontWeight:600, color: color || 'var(--text2)' }}>{sub}</span>}
      </div>
    );
    const ViewAll = ({ onClick }) => <button onClick={onClick} style={{ background:'none', border:'none', color:'var(--accent)', cursor:'pointer', fontSize:12, fontWeight:500 }}>View all →</button>;
    const Panel = ({ title, action, children }) => (
      <div className="card" style={{ padding:0, overflow:'hidden' }}>
        <div className="flex-gap" style={{ justifyContent:'space-between', alignItems:'center', padding:'11px 14px', borderBottom:'1px solid var(--border)' }}>
          <span style={{ fontSize:12, fontWeight:700, color:'var(--text2)', letterSpacing:'0.04em' }}>{title}</span>{action}
        </div>
        <div style={{ padding:'12px 14px' }}>{children}</div>
      </div>
    );
    const TABS = [['overview','Overview'],['phases','Phases & Tasks'],['timeline','Timeline'],['team','Team'],['activity','Activity']];

    return (
      <div>
        <button className="btn btn-secondary btn-sm" onClick={onClose} style={{ marginBottom:12 }}>◀ Projects</button>

        {/* Header */}
        <div className="flex-gap" style={{ justifyContent:'space-between', alignItems:'flex-start', gap:12, flexWrap:'wrap' }}>
          <div>
            <div className="flex-gap" style={{ gap:10, alignItems:'center' }}>
              <span style={{ fontSize:24, fontWeight:700 }}>{d.title}</span>
              {d.lifecycle && <span style={{ fontSize:11, fontWeight:700, padding:'3px 10px', borderRadius:6,
                background: d.lifecycle==='Completed'?'rgba(45,168,102,0.15)':d.lifecycle==='Cancelled'?'var(--bg3)':d.lifecycle==='On Hold'?'rgba(255,153,0,0.15)':'rgba(79,142,247,0.15)',
                color: d.lifecycle==='Completed'?'var(--green)':d.lifecycle==='On Hold'?'#B26A00':d.lifecycle==='Cancelled'?'var(--text2)':'var(--accent)' }}>{d.lifecycle.toUpperCase()}</span>}
            </div>
            <div style={{ fontSize:12.5, color:'var(--text2)', marginTop:4 }}>
              Project ID: <b style={{ color:'var(--text)' }}>{d.project_code || '—'}</b>
              <span style={{ margin:'0 8px' }}>·</span> Lead: <b style={{ color:'var(--text)' }}>{d.owner_name || '—'}</b>
              {d.priority && <><span style={{ margin:'0 8px' }}>·</span> Priority: <span style={{ fontSize:11, fontWeight:700, padding:'2px 8px', borderRadius:6, background:PRI_BG[d.priority], color:PRI_FG[d.priority] }}>{d.priority}</span></>}
            </div>
          </div>
          <div className="flex-gap" style={{ gap:8 }}>
            {isProject && <button className="btn btn-secondary btn-sm" onClick={exportXlsx}>⬇ Export</button>}
            {canEditPlan && <button className="btn btn-secondary" onClick={() => setEditOpen(true)}>✎ Edit Project</button>}
            {canEditPlan && <button className="btn btn-primary" onClick={() => setAddTaskOpen(true)}>+ Add Task</button>}
            {canEditPlan && <button className="btn btn-danger" onClick={openDelete}>🗑 Delete</button>}
          </div>
        </div>

        {/* Summary card */}
        <div className="card" style={{ marginTop:14, marginBottom:14, display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(190px,1fr))', gap:0 }}>
          <div style={{ paddingRight:20 }}>
            <div style={{ fontSize:12, color:'var(--text2)' }}>Start date</div><div style={{ fontWeight:600 }}>{d.start_date ? fmt(d.start_date) : '—'}</div>
            <div style={{ fontSize:12, color:'var(--text2)', marginTop:8 }}>Target date</div><div style={{ fontWeight:600 }}>{d.target_date ? fmt(d.target_date) : '—'}</div>
          </div>
          <div style={{ borderLeft:'1px solid var(--border)', padding:'0 20px' }}>
            <div style={{ fontSize:12, color:'var(--text2)', marginBottom:6 }}>Project members</div>
            <div style={{ display:'flex' }}>{d.members.slice(0,5).map((m,i) => <span key={m.user_id} style={{ marginLeft: i?-6:0 }}><Avatar name={m.full_name} /></span>)}{d.members.length>5 && <span style={{ fontSize:12, color:'var(--text2)', alignSelf:'center', marginLeft:6 }}>+{d.members.length-5}</span>}</div>
            {d.contributing_departments && d.contributing_departments.length>0 && <><div style={{ fontSize:12, color:'var(--text2)', marginTop:8 }}>Contributing departments</div><div style={{ fontSize:12.5 }}>{d.contributing_departments.join(', ')}</div></>}
          </div>
          <div style={{ borderLeft:'1px solid var(--border)', padding:'0 20px' }}>
            <div style={{ fontSize:12, color:'var(--text2)' }}>Overall progress</div>
            <div style={{ fontSize:24, fontWeight:700, color:'var(--green)' }}>{pctT}%</div>
            <div style={{ height:8, background:'var(--bg3)', borderRadius:20, overflow:'hidden', margin:'4px 0' }}><div style={{ width:pctT+'%', height:'100%', background:'var(--green)' }} /></div>
            <div style={{ fontSize:12, color:'var(--text2)' }}>{completed} of {total} tasks completed</div>
          </div>
          <div style={{ borderLeft:'1px solid var(--border)', padding:'0 20px' }}>
            <div style={{ fontSize:12, color:'var(--text2)' }}>Project health</div>
            <div className="flex-gap" style={{ gap:6, alignItems:'center', marginTop:2 }}><span style={{ width:12, height:12, borderRadius:'50%', background:FLAG_COLOR[d.health_flag] }} /><span style={{ fontWeight:700, color:FLAG_COLOR[d.health_flag] }}>{(d.health_flag||'').toUpperCase()}</span></div>
            {d.health_reason && <div style={{ fontSize:12, color:FLAG_COLOR[d.health_flag], marginTop:4 }}>{d.health_reason}</div>}
          </div>
        </div>

        {/* Stat cards */}
        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(150px,1fr))', gap:12, marginBottom:14 }}>
          <StatCard icon="🗒" label="Total tasks" value={total} color="var(--accent)" />
          <StatCard icon="✓" label="Completed" value={completed} sub={pctOf(completed)} color="var(--green)" />
          <StatCard icon="◔" label="In progress" value={inprog} sub={pctOf(inprog)} color="var(--accent)" />
          <StatCard icon="▤" label="Pending" value={pending} sub={pctOf(pending)} color="#B26A00" />
          <StatCard icon="⏰" label="Overdue" value={overdue} sub={pctOf(overdue)} color="var(--red)" />
        </div>

        {/* Tabs */}
        <div className="flex-gap" style={{ gap:0, borderBottom:'1px solid var(--border)', marginBottom:16, flexWrap:'wrap' }}>
          {TABS.map(([tid,label]) => (
            <button key={tid} onClick={() => setTab(tid)} style={{ padding:'9px 18px', fontSize:13, background:'transparent', border:'none', cursor:'pointer', borderBottom: tab===tid ? '2px solid var(--accent)' : '2px solid transparent', color: tab===tid ? 'var(--accent)' : 'var(--text2)', fontWeight: tab===tid ? 600 : 400, marginBottom:-1 }}>{label}</button>
          ))}
        </div>

        {msg && <div style={{ fontSize:13, marginBottom:10, color: msg.startsWith('✓')?'var(--green)':'var(--red)' }}>{msg}</div>}

        {tab === 'overview' && (
          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(240px,1fr))', gap:14, marginBottom:14 }}>
            <Panel title="PHASE PROGRESS">
              {phaseInfo.length ? phaseInfo.map(p => (
                <div key={p.id} style={{ marginBottom:10 }}>
                  <div className="flex-gap" style={{ justifyContent:'space-between', fontSize:12.5, marginBottom:3 }}><span>{p.num}. {p.title}</span><span style={{ color:'var(--text2)' }}>{p.pct}%</span></div>
                  <div className="flex-gap" style={{ gap:8, alignItems:'center' }}>
                    <div style={{ flex:1, height:6, background:'var(--bg3)', borderRadius:20, overflow:'hidden' }}><div style={{ width:p.pct+'%', height:'100%', background: p.pct===100?'var(--green)':p.behind?'var(--red)':'var(--accent)' }} /></div>
                    <StatusPill s={p.st} />
                  </div>
                </div>
              )) : <div style={{ fontSize:12, color:'var(--text2)' }}>No phases yet.</div>}
            </Panel>
            <Panel title="ATTENTION REQUIRED">
              {overdue===0 && phasesBehind.length===0 && dueWeekTasks.length===0 && <div style={{ fontSize:12, color:'var(--text2)' }}>Nothing needs attention. 🎉</div>}
              {overdue>0 && <div className="flex-gap" style={{ gap:8, marginBottom:8 }}><span style={{ color:'var(--red)' }}>⚠</span><div><div style={{ fontSize:13 }}>{overdue} task(s) are overdue</div><div style={{ fontSize:11, color:'var(--text2)' }}>Follow up with the assignees</div></div></div>}
              {phasesBehind.map(p => <div key={p.id} className="flex-gap" style={{ gap:8, marginBottom:8 }}><span style={{ color:'#FF9900' }}>⚠</span><div><div style={{ fontSize:13 }}>{p.title} is behind schedule</div><div style={{ fontSize:11, color:'var(--text2)' }}>Target {fmt(p.end_date)}</div></div></div>)}
              {dueWeekTasks.length>0 && <div className="flex-gap" style={{ gap:8, marginBottom:8 }}><span>📅</span><div><div style={{ fontSize:13 }}>{dueWeekTasks.length} task(s) due this week</div></div></div>}
              {phasesDone.slice(0,1).map(p => <div key={p.id} className="flex-gap" style={{ gap:8 }}><span style={{ color:'var(--green)' }}>✓</span><div><div style={{ fontSize:13 }}>{p.title} completed</div><div style={{ fontSize:11, color:'var(--text2)' }}>Great progress!</div></div></div>)}
            </Panel>
            <Panel title="UPCOMING TASKS" action={<ViewAll onClick={()=>setTab('phases')} />}>
              {upcoming.length ? upcoming.map(t => (
                <div key={t.id} className="flex-gap" style={{ justifyContent:'space-between', gap:8, padding:'5px 0', fontSize:12.5 }}>
                  <span style={{ flex:1, minWidth:0, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{t.title}</span>
                  <span className="flex-gap" style={{ gap:6, alignItems:'center' }}><Avatar name={t.assigned_to_name} size={20} /><span style={{ color: isOverdueT(t)?'var(--red)':'var(--text2)' }}>{t.due_date?fmt(t.due_date):'—'}</span></span>
                </div>
              )) : <div style={{ fontSize:12, color:'var(--text2)' }}>No upcoming dated tasks.</div>}
            </Panel>
            <Panel title="PROJECT DETAILS">
              {d.description && <><div style={{ fontSize:11, color:'var(--text2)' }}>Description</div><div style={{ fontSize:12.5, marginBottom:8 }}>{d.description}</div></>}
              {d.desired_outcome && <><div style={{ fontSize:11, color:'var(--text2)' }}>Desired outcome</div><div style={{ fontSize:12.5, marginBottom:8 }}>{d.desired_outcome}</div></>}
              {d.category && <><div style={{ fontSize:11, color:'var(--text2)' }}>Category</div><div style={{ fontSize:12.5, marginBottom:8 }}>{d.category}</div></>}
              {d.timeline && <><div style={{ fontSize:11, color:'var(--text2)' }}>Timeline</div><div style={{ fontSize:12.5, marginBottom:8 }}>{d.timeline}</div></>}
              <div style={{ fontSize:11, color:'var(--text2)' }}>Status</div><div style={{ fontSize:12.5 }}>{d.lifecycle || 'Active'}</div>
            </Panel>
          </div>
        )}

        {tab === 'overview' && (
          <Panel title="RECENT TASKS" action={<ViewAll onClick={()=>setTab('phases')} />}>
            <div className="table-wrap"><table>
              <thead><tr><th>Task</th><th>Phase</th><th>Assigned To</th><th>Due</th><th>Priority</th><th>Status</th><th>Progress</th></tr></thead>
              <tbody>{recent.map(t => (
                <tr key={t.id}>
                  <td style={{ fontSize:12.5 }}>{t.title}</td>
                  <td style={{ fontSize:12, color:'var(--text2)' }}>{t.phase}</td>
                  <td><span className="flex-gap" style={{ gap:6, alignItems:'center', fontSize:12 }}><Avatar name={t.assigned_to_name} size={20} />{t.assigned_to_name||'—'}</span></td>
                  <td style={{ fontSize:12, color: isOverdueT(t)?'var(--red)':'var(--text2)' }}>{t.due_date?fmt(t.due_date):'—'}</td>
                  <td>{t.priority && <span style={{ fontSize:11, fontWeight:600, padding:'2px 8px', borderRadius:6, background:PRI_BG[t.priority], color:PRI_FG[t.priority] }}>{t.priority}</span>}</td>
                  <td><StatusPill s={t.status} /></td>
                  <td style={{ minWidth:90 }}><div className="flex-gap" style={{ gap:6, alignItems:'center' }}><div style={{ flex:1, height:6, background:'var(--bg3)', borderRadius:20, overflow:'hidden' }}><div style={{ width:taskPct(t)+'%', height:'100%', background: taskPct(t)===100?'var(--green)':'var(--accent)' }} /></div><span style={{ fontSize:11, color:'var(--text2)' }}>{taskPct(t)}%</span></div></td>
                </tr>
              ))}{!recent.length && <tr><td colSpan="7" style={{ fontSize:12, color:'var(--text2)' }}>No tasks yet.</td></tr>}</tbody>
            </table></div>
          </Panel>
        )}

        {tab === 'phases' && <PhasesTasksTab {...{ d, phaseInfo, canEditPlan, canEditTask, renameStep, editStep, editTask, setTaskStatus, addPhase, newPhase, setNewPhase, PLAN_STATUSES, delTask, setMsg, load }} />}
        {tab === 'timeline' && <TimelineTab d={d} phaseInfo={phaseInfo} />}
        {tab === 'team' && <TeamTab d={d} itUsers={itUsers} wl={wl} addMember={addMember} delMember={delMember} canEditPlan={canEditPlan} />}
        {tab === 'activity' && <ActivityTab recent={[...allTasks].sort((a,b)=>String(b.updated_at||'').localeCompare(String(a.updated_at||'')))} />}

        {editOpen && <EditProject id={id} d={d} itUsers={itUsers} cats={cats} onClose={()=>setEditOpen(false)} onDone={()=>{ setEditOpen(false); load(); }} />}
        {addTaskOpen && <AddTaskModal id={id} phases={workItems} members={d.members} onClose={()=>setAddTaskOpen(false)} onDone={()=>{ setAddTaskOpen(false); load(); }} />}
      </div>
    );
  }

  return (
    <div className={fullPage ? '' : 'modal-overlay'} onClick={fullPage ? undefined : (e => e.target===e.currentTarget && false && onClose())}>
      <div className={fullPage ? '' : 'modal'} style={fullPage ? {} : { maxWidth:820, maxHeight:'92vh', overflowY:'auto' }}>
        {fullPage ? (
          <div style={{ marginBottom:12 }}>
            <button className="btn btn-secondary btn-sm" onClick={onClose}>◀ Back to projects</button>
            <div className="flex-gap" style={{ justifyContent:'space-between', alignItems:'center', marginTop:10, gap:8, flexWrap:'wrap' }}>
              <div className="flex-gap" style={{ gap:10, alignItems:'center' }}>
                <span style={{ fontSize:20, fontWeight:700 }}>{isMeeting?'👥':'📁'} {d.title}</span>
                <FlagPill flag={d.health_flag} stale={d.flag_stale} />
              </div>
              {isProject && <button className="btn btn-secondary btn-sm" onClick={exportXlsx}>⬇ Export .xlsx</button>}
            </div>
          </div>
        ) : (
          <div className="modal-header">
            <span className="modal-title">{isMeeting ? '👥' : '📁'} {d.title}</span>
            <div className="flex-gap" style={{ gap:8, alignItems:'center' }}>
              {isProject && <button className="btn btn-secondary btn-sm" onClick={exportXlsx}>⬇ Export .xlsx</button>}
              <button className="modal-close" onClick={onClose}>×</button>
            </div>
          </div>
        )}

        {isProject && (
          <div style={{ fontSize:13, color:'var(--text2)', marginBottom:12 }}>
            <b style={{ color:'var(--text)' }}>{d.percent == null ? '—' : `${d.percent}%`}</b> complete
            {d.lifecycle && <span> · {d.lifecycle}</span>}
            {d.priority && <span> · {d.priority} priority</span>}
            {d.items_total != null && <span> · {d.items_complete}/{d.items_total} steps</span>}
            {d.target_date && <span> · target {fmt(d.target_date)}</span>}
            {d.owner_name && <span> · owner {d.owner_name}</span>}
          </div>
        )}

        {/* Live "what's happening now" cards + roadmap — projects only */}
        {isProject && (
          <>
            <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(150px,1fr))', gap:10, marginBottom:14 }}>
              {[['Current step', curStep],['Active task', curTask],['On it now', curWho]].map(([l,v]) => (
                <div key={l} className="card" style={{ padding:'12px 14px' }}>
                  <div style={{ fontSize:12, color:'var(--text2)' }}>{l}</div>
                  <div style={{ fontSize:16, fontWeight:600, marginTop:2 }}>{v || '—'}</div>
                </div>
              ))}
            </div>
            {roadmap.length > 0 && (
              <div style={{ marginBottom:14 }}>
                <div style={{ fontSize:12, fontWeight:600, color:'var(--text2)', marginBottom:6 }}>ROADMAP</div>
                <div className="flex-gap" style={{ gap:6, flexWrap:'wrap', alignItems:'center' }}>
                  {roadmap.map((r,i) => (
                    <React.Fragment key={i}>
                      <span className="badge" style={{
                        background: r.done ? 'rgba(45,168,102,0.15)' : r.active ? 'rgba(79,142,247,0.12)' : 'var(--bg3)',
                        color: r.done ? 'var(--green)' : r.active ? 'var(--accent)' : 'var(--text2)',
                        border: r.active ? '1px solid var(--accent)' : '1px solid transparent',
                        display:'inline-flex', flexDirection:'column', alignItems:'flex-start', lineHeight:1.3 }}>
                        <span>{r.done ? '✓ ' : ''}{r.title}</span>
                        {(r.start || r.end) && <span style={{ fontSize:10, fontWeight:400, opacity:0.85 }}>{fmtRange(r.start, r.end)}</span>}</span>
                      {i < roadmap.length-1 && <span style={{ color:'var(--text2)' }}>→</span>}
                    </React.Fragment>
                  ))}
                </div>
              </div>
            )}
            {/* Project details — status / priority / category / desired outcome */}
            <div className="card" style={{ marginBottom:14 }}>
              <div style={{ fontSize:12, fontWeight:600, color:'var(--text2)', marginBottom:8 }}>PROJECT DETAILS
                {d.actual_completion_date && <span style={{ fontWeight:400 }}> · completed {fmt(d.actual_completion_date)}</span>}</div>
              <div className="form-row">
                <div className="form-group"><label>Status</label>
                  <select value={det.lifecycle} onChange={e => setDet(s => ({ ...s, lifecycle:e.target.value }))} disabled={!canEditPlan}>
                    {['Draft','Planning','Active','On Hold','Completed','Cancelled'].map(x => <option key={x}>{x}</option>)}
                  </select></div>
                <div className="form-group"><label>Priority</label>
                  <select value={det.priority} onChange={e => setDet(s => ({ ...s, priority:e.target.value }))} disabled={!canEditPlan}>
                    <option value="">—</option>{['Low','Medium','High','Critical'].map(x => <option key={x}>{x}</option>)}
                  </select></div>
                <div className="form-group"><label>Category</label>
                  <input list="proj-cats" value={det.category} onChange={e => setDet(s => ({ ...s, category:e.target.value }))} disabled={!canEditPlan} placeholder="e.g. Infrastructure" />
                  <datalist id="proj-cats">{cats.map(c => <option key={c} value={c} />)}</datalist></div>
              </div>
              <div className="form-group"><label>Desired outcome</label>
                <textarea rows={2} value={det.desired_outcome} onChange={e => setDet(s => ({ ...s, desired_outcome:e.target.value }))} disabled={!canEditPlan} placeholder="What good looks like when this project is done" /></div>
              {canEditPlan && <button className="btn btn-primary btn-sm" onClick={() => saveDetails(false)}>Save details</button>}
            </div>

            {/* Editable timeline (free text — imported from the sheet's Timeline column) */}
            <div className="card" style={{ marginBottom:14 }}>
              <div style={{ fontSize:12, fontWeight:600, color:'var(--text2)', marginBottom:8 }}>TIMELINE</div>
              <div className="flex-gap" style={{ gap:8, flexWrap:'wrap', alignItems:'center' }}>
                <input value={tl} onChange={e => setTl(e.target.value)} placeholder="e.g. Aug–Sep 2026"
                  disabled={!canEditPlan} style={{ flex:1, minWidth:200, padding:'6px 8px' }} />
                {canEditPlan && <button className="btn btn-primary btn-sm" onClick={saveTimeline}>Save</button>}
              </div>
            </div>
          </>
        )}

        {/* Health flag */}
        <div className="card" style={{ marginBottom:14, borderLeft:`3px solid ${FLAG_COLOR[flag.health_flag]}` }}>
          <div style={{ fontSize:12, fontWeight:600, color:'var(--text2)', marginBottom:8 }}>HEALTH FLAG {d.flag_stale && <span style={{ color:'#FF9900' }}>· stale (&gt;14 days)</span>}</div>
          <div className="flex-gap" style={{ gap:8, flexWrap:'wrap', alignItems:'center' }}>
            <select value={flag.health_flag} onChange={e => setFlag(f => ({ ...f, health_flag:e.target.value }))} style={{ padding:'6px 8px' }}>
              {['green','amber','red'].map(x => <option key={x} value={x}>{cap(x)}</option>)}
            </select>
            <input placeholder="Reason (required if not green)" value={flag.health_reason}
              onChange={e => setFlag(f => ({ ...f, health_reason:e.target.value }))} style={{ flex:1, minWidth:200, padding:'6px 8px' }} />
            <button className="btn btn-primary btn-sm" onClick={saveFlag}>Save flag</button>
          </div>
          <div style={{ fontSize:11, color:'var(--text2)', marginTop:6 }}>Last set: {fmt(d.health_updated_at)}</div>
        </div>

        {/* Members = visibility exception */}
        <div className="card" style={{ marginBottom:14 }}>
          <div style={{ fontSize:12, fontWeight:600, color:'var(--text2)', marginBottom:8 }}>MEMBERS <span style={{ fontWeight:400 }}>— can see every task under this container</span></div>
          <div className="flex-gap" style={{ gap:6, flexWrap:'wrap', alignItems:'center' }}>
            {d.members.map(m => (
              <span key={m.user_id} className="badge badge-normal" style={{ display:'inline-flex', gap:4 }}>
                {m.full_name}<button onClick={() => delMember(m.user_id)} style={{ background:'none', border:'none', cursor:'pointer', color:'var(--red)' }}>×</button>
              </span>
            ))}
            <select defaultValue="" onChange={e => { addMember(e.target.value); e.target.value=''; }} style={{ padding:'4px 6px' }}>
              <option value="">+ add member…</option>
              {itUsers.filter(u => !d.members.some(m => m.user_id===u.id)).map(u => <option key={u.id} value={u.id}>{u.full_name}</option>)}
            </select>
          </div>
        </div>

        {/* Per-project member workload */}
        {isProject && wl.length > 0 && (
          <div className="card" style={{ padding:0, overflow:'hidden', marginBottom:14 }}>
            <div style={{ fontSize:12, fontWeight:600, color:'var(--text2)', padding:'10px 12px' }}>MEMBER WORKLOAD (this project)</div>
            <div className="table-wrap"><table>
              <thead><tr><th>Member</th><th>Pending</th><th>Overdue</th><th>Due this week</th></tr></thead>
              <tbody>
                {wl.map(w => (
                  <tr key={w.id}>
                    <td style={{ fontWeight:500 }}>{w.full_name}</td>
                    <td>{w.pending}</td>
                    <td style={{ color: Number(w.overdue) > 0 ? 'var(--red)' : 'var(--text2)' }}>{w.overdue}</td>
                    <td style={{ color: Number(w.due_week) > 0 ? '#FF9900' : 'var(--text2)' }}>{w.due_week}</td>
                  </tr>
                ))}
              </tbody>
            </table></div>
          </div>
        )}

        {/* Project plan: step -> task -> assigned -> status -> completed on */}
        {isProject && (
          <div className="card" style={{ padding:0, overflow:'hidden', marginBottom:14 }}>
            <div style={{ fontSize:12, fontWeight:600, color:'var(--text2)', padding:'10px 12px' }}>PROJECT PLAN</div>
            {canEditPlan && <div style={{ fontSize:11, color:'var(--text2)', padding:'0 12px 8px' }}>Click a step or task to rename it; change the assignee or status from the dropdowns.</div>}
            <div className="table-wrap"><table>
              <thead><tr><th style={{ width:24 }}></th><th>Task</th><th>Assigned To</th><th>Due</th><th>Status</th><th>Done</th></tr></thead>
              <tbody>
                {d.items.map((it, idx) => {
                  const step = it.sequence_no || idx + 1;
                  const ts = it.tasks || [];
                  const overdue = t => t.due_date && String(t.due_date).slice(0,10) < new Date().toISOString().slice(0,10) && !['Completed','Closed'].includes(t.status);
                  return (
                    <React.Fragment key={it.id}>
                      {/* Phase header band — all phase-level fields on one aligned row */}
                      <tr style={{ background:'var(--bg3)' }}>
                        <td colSpan={6} style={{ padding:'8px 10px' }}>
                          <div className="flex-gap" style={{ gap:8, alignItems:'center', flexWrap:'wrap' }}>
                            <span style={{ color:'var(--text2)', fontSize:11, fontWeight:600 }}>{step}.</span>
                            {canEditPlan
                              ? <input key={it.title} defaultValue={it.title} onBlur={e => renameStep(it, e.target.value)} style={{ fontSize:13, fontWeight:600, padding:'3px 6px', minWidth:150 }} />
                              : <b style={{ fontSize:13 }}>{it.title}</b>}
                            {canEditPlan ? (<>
                              <span style={{ fontSize:11, color:'var(--text2)' }}>Dates</span>
                              <input type="date" title="Phase start" value={it.start_date ? String(it.start_date).slice(0,10) : ''} onChange={e => editStep(it, { start_date: e.target.value || null })} style={{ fontSize:11, padding:'2px 4px' }} />
                              <span style={{ color:'var(--text2)' }}>–</span>
                              <input type="date" title="Phase end" value={it.end_date ? String(it.end_date).slice(0,10) : ''} onChange={e => editStep(it, { end_date: e.target.value || null })} style={{ fontSize:11, padding:'2px 4px' }} />
                              <select value={it.phase_status || ''} onChange={e => editStep(it, { phase_status: e.target.value || null })} style={{ fontSize:11, padding:'2px 4px' }}>
                                <option value="">— status —</option>{['Not Started','In Progress','On Hold','Completed'].map(s => <option key={s}>{s}</option>)}
                              </select>
                              <select value={it.phase_lead_user_id || ''} onChange={e => editStep(it, { phase_lead_user_id: e.target.value || null })} style={{ fontSize:11, padding:'2px 4px' }}>
                                <option value="">— lead —</option>{d.members.map(m => <option key={m.user_id} value={m.user_id}>{m.full_name}</option>)}
                              </select>
                            </>) : (
                              <span style={{ fontSize:11, color:'var(--text2)' }}>
                                {[fmtRange(it.start_date, it.end_date), it.phase_status, it.phase_lead_name && ('Lead: ' + it.phase_lead_name)].filter(Boolean).join(' · ')}
                              </span>
                            )}
                          </div>
                        </td>
                      </tr>
                      {/* Task rows — aligned columns under the phase */}
                      {!ts.length ? (
                        <tr><td style={{ width:24 }}></td><td colSpan={5} style={{ color:'var(--text2)', fontSize:12 }}>No tasks yet — use “+ Add task” below.</td></tr>
                      ) : ts.map(t => (
                        <tr key={t.id}>
                          <td style={{ width:24 }}></td>
                          <td style={{ fontSize:12 }}>
                            {canEditPlan
                              ? <input key={t.title} defaultValue={t.title} onBlur={e => { const v = e.target.value.trim(); if (v && v !== t.title) editTask(t, { title:v }); }} style={{ fontSize:12, padding:'2px 4px', width:'100%', minWidth:120 }} />
                              : t.title}
                            {t.campus_name && <span style={{ color:'var(--text2)' }}> · {t.campus_name}</span>}
                          </td>
                          <td style={{ fontSize:12 }}>
                            {canEditPlan
                              ? <select value={t.assigned_to || ''} onChange={e => editTask(t, { assigned_to: e.target.value || null })} style={{ fontSize:12, padding:'2px 4px' }}>
                                  <option value="">— unassigned —</option>
                                  {d.members.map(m => <option key={m.user_id} value={m.user_id}>{m.full_name}</option>)}
                                  {t.assigned_to && !d.members.some(m => m.user_id === t.assigned_to) && <option value={t.assigned_to}>{t.assigned_to_name}</option>}
                                </select>
                              : (t.assigned_to_name || <span style={{ color:'var(--text2)' }}>—</span>)}
                          </td>
                          <td style={{ fontSize:12 }}>
                            {canEditPlan
                              ? <input type="date" value={t.due_date ? String(t.due_date).slice(0,10) : ''} onChange={e => editTask(t, { due_date: e.target.value || null })} style={{ fontSize:11, padding:'1px 3px' }} />
                              : t.due_date ? <span style={{ color: overdue(t) ? 'var(--red)' : 'var(--text2)' }}>{fmt(t.due_date)}</span> : '—'}
                          </td>
                          <td>{canEditTask(t)
                            ? <select value={PLAN_STATUSES.includes(t.status) ? t.status : 'Pending'} onChange={e => setTaskStatus(t, e.target.value)} style={{ fontSize:12, padding:'2px 4px' }}>
                                {PLAN_STATUSES.map(s => <option key={s}>{s}</option>)}
                              </select>
                            : <span className={`badge ${['Completed','Closed'].includes(t.status)?'badge-completed':'badge-pending'}`}>{t.status}</span>}</td>
                          <td style={{ fontSize:12, color:'var(--text2)' }}>{t.completed_at ? fmt(t.completed_at) : '—'}</td>
                        </tr>
                      ))}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table></div>
            {canEditPlan && (
              <div className="flex-gap" style={{ gap:8, padding:'10px 12px', borderTop:'1px solid var(--border)', flexWrap:'wrap', alignItems:'flex-end' }}>
                <div style={{ flex:1, minWidth:160 }}>
                  <div style={{ fontSize:11, color:'var(--text2)', marginBottom:2 }}>Phase / step</div>
                  <input value={newPhase.title} onChange={e => setNewPhase(s => ({ ...s, title:e.target.value }))} placeholder="New phase name"
                    onKeyDown={e => { if (e.key === 'Enter') addPhase(); }} style={{ width:'100%', padding:'6px 8px' }} />
                </div>
                <div><div style={{ fontSize:11, color:'var(--text2)', marginBottom:2 }}>Start</div>
                  <input type="date" value={newPhase.start_date} onChange={e => setNewPhase(s => ({ ...s, start_date:e.target.value }))} style={{ padding:'6px 8px' }} /></div>
                <div><div style={{ fontSize:11, color:'var(--text2)', marginBottom:2 }}>End</div>
                  <input type="date" value={newPhase.end_date} onChange={e => setNewPhase(s => ({ ...s, end_date:e.target.value }))} style={{ padding:'6px 8px' }} /></div>
                <button className="btn btn-primary btn-sm" onClick={addPhase}>+ Add phase</button>
              </div>
            )}
          </div>
        )}

        {/* Open points carried forward from earlier meetings of the same kind */}
        {isMeeting && carry.length > 0 && (
          <div style={{ marginBottom:14 }}>
            <div style={{ fontSize:12, fontWeight:600, color:'#FF9900', marginBottom:8 }}>⏳ OPEN FROM PREVIOUS MEETINGS ({carry.length})</div>
            {carry.map(it => (
              <div key={it.id} className="card" style={{ marginBottom:6, borderLeft:'3px solid #FF9900' }}>
                <div className="flex-gap" style={{ justifyContent:'space-between', gap:8, flexWrap:'wrap' }}>
                  <div style={{ flex:1, minWidth:200 }}>
                    <span style={{ fontWeight:500 }}>{it.title}</span>
                    {it.category && <span className="badge badge-normal" style={{ marginLeft:8 }}>{catLabel(it.category)}</span>}
                    <div style={{ fontSize:11, color:'var(--text2)' }}>from “{it.meeting_title}” · {fmt(it.meeting_date)}</div>
                    {it.category === 'policy_change'
                      ? <div style={{ fontSize:12 }}>{it.acks_done} of {it.acks_total} acknowledged</div>
                      : <div style={{ fontSize:12 }}>{it.tasks_complete} of {it.tasks_total} complete{it.pending_campuses?.length>0 ? ` · pending: ${it.pending_campuses.join(', ')}` : ''}</div>}
                  </div>
                  <button className="btn btn-secondary btn-sm" onClick={() => closePoint(it, true)}>Close</button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Items */}
        <div style={{ fontSize:12, fontWeight:600, color:'var(--text2)', marginBottom:8 }}>{isMeeting?'DISCUSSION POINTS':'STEPS — add & fan out'}</div>
        {d.items.map(it => (
          <div key={it.id} className="card" style={{ marginBottom:8 }}>
            <div className="flex-gap" style={{ justifyContent:'space-between', gap:8, flexWrap:'wrap' }}>
              <div style={{ flex:1, minWidth:200 }}>
                <span style={{ fontWeight:500 }}>{it.title}</span>
                {it.category && <span className="badge badge-normal" style={{ marginLeft:8 }}>{catLabel(it.category)}</span>}
                {it.description && <div style={{ fontSize:12, color:'var(--text2)', marginTop:2 }}>{it.description}</div>}
                {isWork(it) && (
                  <div style={{ fontSize:12, marginTop:4, color: it.tasks_total>0 && it.tasks_complete==it.tasks_total ? 'var(--green)' : 'var(--text2)' }}>
                    {it.tasks_total>0 ? `${it.tasks_complete} of ${it.tasks_total} complete` : 'No tasks yet'}
                    {it.pending_campuses?.length>0 && <span> · pending: {it.pending_campuses.join(', ')}</span>}
                  </div>
                )}
                {it.category === 'policy_change' && (
                  <div style={{ fontSize:12, marginTop:4, color: it.acks_total>0 && it.acks_done==it.acks_total ? 'var(--green)' : 'var(--text2)' }}>
                    {it.acks_total>0 ? `${it.acks_done} of ${it.acks_total} acknowledged` : 'No acknowledgers'}
                    {it.pending_acks?.length>0 && <span> · pending: {it.pending_acks.join(', ')}</span>}
                  </div>
                )}
                {it.closed_at && <span className="badge badge-normal" style={{ marginTop:4, fontSize:10 }}>Closed</span>}
              </div>
              <div className="flex-gap" style={{ gap:6, alignItems:'flex-start' }}>
                {it.my_pending_ack && <button className="btn btn-primary btn-sm" onClick={() => acknowledge(it)}>Acknowledge</button>}
                {isMeeting && isWork(it) && <button className="btn btn-secondary btn-sm" onClick={() => setFanFor(fanFor===it.id?null:it.id)}>Fan-out</button>}
                {isProject && isWork(it) && <button className="btn btn-primary btn-sm" onClick={() => setAddFor(addFor===it.id?null:it.id)}>+ Add task</button>}
                {isWork(it) && it.tasks_total>0 && <button className="btn btn-secondary btn-sm" onClick={() => openTasks(it)}>{tasksFor===it.id?'Hide':'Tasks'}</button>}
                {isMeeting && <button className="btn btn-secondary btn-sm" onClick={() => closePoint(it, !it.closed_at)}>{it.closed_at?'Reopen':'Close'}</button>}
                <button className="btn btn-danger btn-sm" onClick={() => delItem(it)}>Delete</button>
              </div>
            </div>

            {fanFor === it.id && <FanOut item={it} campuses={campuses} onDone={() => { setFanFor(null); load(); }} setMsg={setMsg} />}
            {addFor === it.id && <AddProjectTask item={it} members={d.members} onDone={() => { setAddFor(null); load(); }} setMsg={setMsg} />}
            {tasksFor === it.id && (
              <div style={{ marginTop:8, borderTop:'1px solid var(--border)', paddingTop:8 }}>
                {tasks.map(t => (
                  <div key={t.id} className="flex-gap" style={{ justifyContent:'space-between', fontSize:12, padding:'3px 0' }}>
                    <span>{t.campus_name} · <span className="mono">{t.task_id}</span> · {t.status}{t.assigned_to_name?` · ${t.assigned_to_name}`:''}</span>
                    <button className="btn btn-danger btn-sm" onClick={() => delTask(t)}>Delete</button>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}

        {/* Add item */}
        <div className="card" style={{ marginTop:8 }}>
          <div className="form-row">
            <div className="form-group" style={{ flex:2 }}><label>New {isMeeting?'point':'step'}</label>
              <input value={newItem.title} onChange={e => setNewItem(s => ({ ...s, title:e.target.value }))} placeholder="Title" /></div>
            {isMeeting && (
              <div className="form-group"><label>Category</label>
                <select value={newItem.category} onChange={e => setNewItem(s => ({ ...s, category:e.target.value }))}>
                  {CATS.map(([v,l]) => <option key={v} value={v}>{l}</option>)}
                </select></div>
            )}
          </div>
          <div className="form-group"><label>Description</label>
            <textarea rows={2} value={newItem.description} onChange={e => setNewItem(s => ({ ...s, description:e.target.value }))} /></div>

          {isMeeting && newItem.category === 'task' && (
            <div className="form-group">
              <div className="flex-gap" style={{ justifyContent:'space-between' }}>
                <label>Campuses — one task each, auto-assigned</label>
                <div className="flex-gap" style={{ gap:6 }}>
                  <button className="btn btn-secondary btn-sm" onClick={() => setPointCampus(Object.fromEntries(campuses.map(c=>[c.id,true])))}>All {campuses.length}</button>
                  <button className="btn btn-secondary btn-sm" onClick={() => setPointCampus({})}>None</button>
                </div>
              </div>
              <div style={{ display:'flex', flexWrap:'wrap', gap:8, maxHeight:120, overflowY:'auto', border:'1px solid var(--border)', borderRadius:'var(--radius)', padding:8 }}>
                {campuses.map(c => (
                  <label className="opt" key={c.id} style={{ display:'flex', alignItems:'center', gap:4, fontSize:12 }}>
                    <input type="checkbox" checked={!!pointCampus[c.id]} onChange={() => setPointCampus(s => ({ ...s, [c.id]:!s[c.id] }))} /> {c.name}
                  </label>
                ))}
              </div>
            </div>
          )}
          {isMeeting && newItem.category === 'policy_change' && (
            <div className="form-group">
              <div className="flex-gap" style={{ justifyContent:'space-between' }}>
                <label>IT admins who must acknowledge</label>
                <div className="flex-gap" style={{ gap:6 }}>
                  <button className="btn btn-secondary btn-sm" onClick={() => setPointUsers(Object.fromEntries(itUsers.map(u=>[u.id,true])))}>All</button>
                  <button className="btn btn-secondary btn-sm" onClick={() => setPointUsers({})}>None</button>
                </div>
              </div>
              <div style={{ display:'flex', flexWrap:'wrap', gap:8, maxHeight:120, overflowY:'auto', border:'1px solid var(--border)', borderRadius:'var(--radius)', padding:8 }}>
                {itUsers.map(u => (
                  <label className="opt" key={u.id} style={{ display:'flex', alignItems:'center', gap:4, fontSize:12 }}>
                    <input type="checkbox" checked={!!pointUsers[u.id]} onChange={() => setPointUsers(s => ({ ...s, [u.id]:!s[u.id] }))} /> {u.full_name}
                  </label>
                ))}
              </div>
            </div>
          )}
          {isMeeting && <div style={{ fontSize:11, color:'var(--text2)', marginBottom:8 }}>
            <b>Task</b> → one task per campus. <b>Information</b> → recorded only. <b>Policy Change</b> → acknowledgement per selected IT admin (+ added to the “IT Policy for IT Person” doc).</div>}
          <button className="btn btn-primary btn-sm" onClick={addItem}>+ Add</button>
        </div>

        {msg && <div style={{ fontSize:13, marginTop:10, color: msg.startsWith('✓')?'var(--green)':'var(--red)' }}>{msg}</div>}
      </div>
    </div>
  );
}

function FanOut({ item, campuses, onDone, setMsg }) {
  const [sel, setSel] = useState({});
  const [busy, setBusy] = useState(false);
  const chosen = Object.keys(sel).filter(k => sel[k]).map(Number);
  const go = async () => {
    if (!chosen.length) return;
    setBusy(true);
    try { const r = await api.post(`/containers/items/${item.id}/fanout`, { campus_ids: chosen });
      setMsg(`✓ Created ${r.data.created} task(s)`); onDone(); }
    catch (e) { setMsg(e.response?.data?.error || 'Fan-out failed'); } finally { setBusy(false); }
  };
  return (
    <div style={{ marginTop:8, borderTop:'1px solid var(--border)', paddingTop:8 }}>
      <div style={{ fontSize:12, color:'var(--text2)', marginBottom:6 }}>Generate one task per campus (auto-assigned to that campus’s IT person):</div>
      <div className="flex-gap" style={{ gap:6, flexWrap:'wrap', marginBottom:8 }}>
        <button className="btn btn-secondary btn-sm" onClick={() => setSel(Object.fromEntries(campuses.map(c=>[c.id,true])))}>All</button>
        <button className="btn btn-secondary btn-sm" onClick={() => setSel({})}>None</button>
      </div>
      <div style={{ display:'flex', flexWrap:'wrap', gap:8, marginBottom:8 }}>
        {campuses.map(c => (
          <label className="opt" key={c.id} style={{ display:'flex', alignItems:'center', gap:4, fontSize:12 }}>
            <input type="checkbox" checked={!!sel[c.id]} onChange={() => setSel(s => ({ ...s, [c.id]:!s[c.id] }))} /> {c.name}
          </label>
        ))}
      </div>
      <button className="btn btn-primary btn-sm" disabled={busy || !chosen.length} onClick={go}>{busy?'…':`Fan-out to ${chosen.length} campus(es)`}</button>
    </div>
  );
}

// Project step: add one task and assign it to a container member (no campus fan-out).
function AddProjectTask({ item, members, onDone, setMsg }) {
  const [f, setF] = useState({ title:'', description:'', assigned_to:'', priority:'Normal', due_date:'' });
  const [busy, setBusy] = useState(false);
  const go = async () => {
    if (!f.title.trim())   { setMsg('Task title required'); return; }
    if (!f.assigned_to)    { setMsg('Pick a member to assign'); return; }
    setBusy(true);
    try { await api.post(`/containers/items/${item.id}/task`, f); setMsg('✓ Task added'); onDone(); }
    catch (e) { setMsg(e.response?.data?.error || 'Failed to add task'); } finally { setBusy(false); }
  };
  return (
    <div style={{ marginTop:8, borderTop:'1px solid var(--border)', paddingTop:8 }}>
      {!members?.length ? (
        <div style={{ fontSize:12, color:'var(--text2)' }}>Add members to this project first (Members, above), then assign tasks to them.</div>
      ) : (<>
        <div className="form-row">
          <div className="form-group" style={{ flex:2 }}><label>Task</label>
            <input value={f.title} onChange={e=>setF(s=>({...s,title:e.target.value}))} placeholder="What needs doing"
              onKeyDown={e=>{ if(e.key==='Enter') go(); }} /></div>
          <div className="form-group"><label>Assign to</label>
            <select value={f.assigned_to} onChange={e=>setF(s=>({...s,assigned_to:e.target.value}))}>
              <option value="">— member —</option>
              {members.map(m => <option key={m.user_id} value={m.user_id}>{m.full_name}</option>)}
            </select></div>
          <div className="form-group"><label>Priority</label>
            <select value={f.priority} onChange={e=>setF(s=>({...s,priority:e.target.value}))}>
              {['Low','Normal','High'].map(p=><option key={p}>{p}</option>)}
            </select></div>
          <div className="form-group"><label>Due date</label>
            <input type="date" value={f.due_date} onChange={e=>setF(s=>({...s,due_date:e.target.value}))} /></div>
        </div>
        <div className="form-group"><label>Description <span style={{ fontSize:11, color:'var(--text2)' }}>(optional)</span></label>
          <textarea rows={2} value={f.description} onChange={e=>setF(s=>({...s,description:e.target.value}))} /></div>
        <button className="btn btn-primary btn-sm" disabled={busy} onClick={go}>{busy?'…':'Add task'}</button>
      </>)}
    </div>
  );
}

// Bulk-import projects from a spreadsheet (.xlsx / .csv exported from Google Sheets).
const IMPORT_COLS = 'Project Name, Description, Desired Outcome, Project Lead, Project Members, Contributing Members from other Department, Timeline, Roadmap';
const HEADER_ALIASES = {
  project:['project','project name','project title'],
  description:['description','desc','details'],
  desired_outcome:['desired outcome','outcome','goal','objective','expected outcome'],
  project_lead:['project lead','lead','project owner','owner'],
  project_members:['project members','members','team','team members'],
  contributing_members:['contributing members from other department','contributing members','contributing members from other dept','other department','other dept','cross-department','contributors','contributing'],
  timeline:['timeline','time line'],
  roadmap:['roadmap','road map','plan'],
  start_date:['start date','start_date','start','begin'],
  target_date:['target date','target_date','due','due date','target','end date','deadline'],
  step:['step','phase','stage','milestone'],
  task:['task','task title','activity','action','todo','to-do'],
  assignee:['assignee','assign to','assigned to','responsible','person'],
  priority:['priority','pri'],
};
function mapHeaders(rawKeys) {
  const map = {};
  for (const key of rawKeys) {
    const k = String(key).trim().toLowerCase();
    for (const [field, aliases] of Object.entries(HEADER_ALIASES))
      if (aliases.includes(k)) { map[field] = key; break; }
  }
  return map;
}

function ImportProjects({ onClose, onDone }) {
  const [step, setStep] = useState('pick');   // pick -> preview -> result
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState({ projects:0, steps:0, tasks:0 });
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const downloadTemplate = () => {
    const aoa = [
      ['S.No','Project Name','Description','Desired Outcome','Project Lead','Project Members','Contributing Members from other Department','Timeline','Roadmap'],
      [1,'Lab 3 Refresh','Replace 20 ageing desktops','Modern lab, zero downtime','Surya','Selvakamatchi, Jayasurya','Facilities Head','Aug–Sep 2026','Quotes → PO → Install → Handover'],
      [2,'CCTV Upgrade','New NVRs across campus','Full coverage, 30-day retention','Selvakamatchi','Surya','','Aug–Oct 2026','Survey → Procure → Install'],
    ];
    const ws = XLSX.utils.aoa_to_sheet(aoa);
    const wb = XLSX.utils.book_new(); XLSX.utils.book_append_sheet(wb, ws, 'Projects');
    XLSX.writeFile(wb, 'projects-import-template.xlsx');
  };

  const handleFile = e => {
    const file = e.target.files[0]; if (!file) return;
    setErr('');
    const reader = new FileReader();
    reader.onload = evt => {
      try {
        const wb = XLSX.read(evt.target.result, { type:'array' });
        const ws = wb.Sheets[wb.SheetNames[0]];
        const raw = XLSX.utils.sheet_to_json(ws, { defval:'' });
        if (!raw.length) { setErr('The sheet has no data rows.'); return; }
        const hmap = mapHeaders(Object.keys(raw[0]));
        if (!hmap.project) { setErr(`Couldn't find a "Project" column. Expected columns: ${IMPORT_COLS}`); return; }
        const mapped = raw.map(r => {
          const o = {};
          for (const [field, srcKey] of Object.entries(hmap)) o[field] = r[srcKey];
          return o;
        }).filter(r => String(r.project||'').trim());
        const splitPeople = v => String(v||'').split(/[,;/\n&]|\band\b/i).map(s => s.trim()).filter(Boolean);
        const splitRoadmap = v => String(v||'').split(/\s*(?:→|-+>|=+>|—+>|»|➜)\s*/).map(s => s.trim()).filter(Boolean);
        const projects = new Set(mapped.map(r => String(r.project).trim()));
        const steps = new Set();
        mapped.forEach(r => {
          if (String(r.step||'').trim()) steps.add(r.project+'||'+r.step);
          splitRoadmap(r.roadmap).forEach(s => steps.add(r.project+'||'+s));
        });
        const tasks = mapped.filter(r => String(r.task||'').trim()).length;
        const people = new Set();
        mapped.forEach(r => [r.project_lead, r.project_members, r.contributing_members]
          .forEach(c => splitPeople(c).forEach(n => people.add(n.toLowerCase()))));
        setRows(mapped);
        setSummary({ projects: projects.size, steps: steps.size, tasks, people: people.size });
        setStep('preview');
      } catch (e) { setErr('Could not read the file. Save it as .xlsx or .csv and try again.'); }
    };
    reader.readAsArrayBuffer(file);
  };

  const doUpload = async () => {
    setBusy(true); setErr('');
    try { const r = await api.post('/containers/import', { rows }); setResult(r.data); setStep('result'); }
    catch (e) { setErr(e.response?.data?.error || 'Import failed'); }
    finally { setBusy(false); }
  };

  return (
    <div className="modal-overlay" onClick={e => e.target===e.currentTarget && false && onClose()}>
      <div className="modal" style={{ maxWidth:560 }}>
        <div className="modal-header"><span className="modal-title">⬆ Bulk Upload Projects</span>
          <button className="modal-close" onClick={onClose}>×</button></div>

        {step === 'pick' && (
          <div>
            <p style={{ fontSize:13, color:'var(--text2)' }}>
              Upload an <b>.xlsx</b> or <b>.csv</b> (export your Google Sheet). Recognised columns:
            </p>
            <div style={{ fontSize:12, background:'var(--bg3)', border:'1px solid var(--border)', borderRadius:'var(--radius)', padding:'8px 10px', margin:'6px 0 12px' }}>{IMPORT_COLS}</div>
            <ul style={{ fontSize:12.5, color:'var(--text2)', margin:'0 0 12px', paddingLeft:18, lineHeight:1.6 }}>
              <li>One row per project. <b>Project Lead</b> becomes the owner; <b>Project Members</b> &amp; <b>Contributing Members</b> (comma/&amp;-separated) are added as members.</li>
              <li>People match active users by name/username/email; unmatched names are reported, not guessed.</li>
              <li><b>Desired Outcome</b> &amp; <b>Timeline</b> fold into the description. <b>Roadmap</b> splits on arrows (<b>→</b>) into project <b>Steps</b>.</li>
            </ul>
            <div className="flex-gap" style={{ gap:8 }}>
              <button className="btn btn-secondary btn-sm" onClick={downloadTemplate}>⬇ Download template</button>
              <label className="btn btn-primary btn-sm" style={{ cursor:'pointer' }}>
                Choose file…
                <input type="file" accept=".xlsx,.xls,.csv" onChange={handleFile} style={{ display:'none' }} />
              </label>
            </div>
            {err && <div className="err-msg" style={{ marginTop:10 }}>{err}</div>}
          </div>
        )}

        {step === 'preview' && (
          <div>
            <p style={{ fontSize:13 }}>Ready to import:</p>
            <div className="flex-gap" style={{ gap:12, margin:'10px 0 14px' }}>
              {[['Projects',summary.projects],['People',summary.people],['Steps',summary.steps],['Tasks',summary.tasks]]
                .filter(([l,n]) => l==='Projects' || l==='People' || n>0).map(([l,n]) => (
                <div key={l} className="card" style={{ padding:'12px 16px', textAlign:'center', flex:1 }}>
                  <div style={{ fontSize:24, fontWeight:700 }}>{n}</div><div style={{ fontSize:12, color:'var(--text2)' }}>{l}</div>
                </div>
              ))}
            </div>
            <div style={{ fontSize:12, color:'var(--text2)' }}>Existing open projects with the same title are reused (steps/tasks appended). Unmatched assignees are reported after import.</div>
            {err && <div className="err-msg" style={{ marginTop:10 }}>{err}</div>}
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => { setStep('pick'); setRows([]); }}>Back</button>
              <button className="btn btn-primary" disabled={busy} onClick={doUpload}>{busy?'Importing…':`Import ${summary.projects} project(s)`}</button>
            </div>
          </div>
        )}

        {step === 'result' && result && (
          <div>
            <div style={{ fontSize:14, color:'var(--green)', fontWeight:600, marginBottom:8 }}>✓ Import complete</div>
            <div style={{ fontSize:13, marginBottom:10 }}>Created <b>{result.projects}</b> project(s){result.members > 0 && <>, <b>{result.members}</b> member link(s)</>}{result.steps > 0 && <>, <b>{result.steps}</b> step(s)</>}{result.tasks > 0 && <>, <b>{result.tasks}</b> task(s)</>}.
              {result.skipped > 0 && <span style={{ color:'var(--text2)' }}> {result.skipped} task(s) already existed — skipped.</span>}</div>
            {result.warnings?.length > 0 && (
              <div style={{ fontSize:12, color:'var(--text2)' }}>
                <div style={{ fontWeight:600, color:'var(--orange)', marginBottom:4 }}>⚠ {result.warnings.length} note(s):</div>
                <ul style={{ margin:0, paddingLeft:18, maxHeight:160, overflowY:'auto' }}>
                  {result.warnings.map((w,i) => <li key={i}>{w}</li>)}
                </ul>
              </div>
            )}
            <div className="modal-footer">
              <button className="btn btn-primary" onClick={onDone}>Done</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Full-page project dashboard tab components ──────────────────────────────
const dfmt = x => x ? new Date(x).toLocaleDateString('en-IN', { day:'2-digit', month:'short', year:'2-digit' }) : '—';

function PhasesTasksTab({ d, canEditPlan, canEditTask, renameStep, editStep, editTask, setTaskStatus, addPhase, newPhase, setNewPhase, PLAN_STATUSES, setMsg, load }) {
  const [addFor, setAddFor] = React.useState(null);
  const delT = async t => { if (window.confirm(`Delete task ${t.task_id}?`)) { try { await api.delete(`/tasks/${t.id}`); load(); } catch (e) { setMsg('Delete failed'); } } };
  return (
    <div className="card" style={{ padding:0, overflow:'hidden', marginBottom:14 }}>
      <div style={{ fontSize:12, fontWeight:600, color:'var(--text2)', padding:'10px 12px' }}>PROJECT PLAN
        {canEditPlan && <span style={{ fontWeight:400 }}> — click a phase or task to rename; set assignee, due date and status from the dropdowns.</span>}</div>
      <div className="table-wrap"><table>
        <thead><tr><th style={{ width:24 }}></th><th>Task</th><th>Assigned To</th><th>Due</th><th>Status</th><th>Progress</th><th></th></tr></thead>
        <tbody>
          {d.items.filter(isWork).map((it, idx) => {
            const step = it.sequence_no || idx + 1;
            const ts = it.tasks || [];
            return (
              <React.Fragment key={it.id}>
                <tr style={{ background:'var(--bg3)' }}>
                  <td colSpan={7} style={{ padding:'8px 10px' }}>
                    <div className="flex-gap" style={{ gap:8, alignItems:'center', flexWrap:'wrap' }}>
                      <span style={{ color:'var(--text2)', fontSize:11, fontWeight:600 }}>{step}.</span>
                      {canEditPlan
                        ? <input key={it.title} defaultValue={it.title} onBlur={e => renameStep(it, e.target.value)} style={{ fontSize:13, fontWeight:600, padding:'3px 6px', minWidth:150 }} />
                        : <b style={{ fontSize:13 }}>{it.title}</b>}
                      {canEditPlan && <>
                        <span style={{ fontSize:11, color:'var(--text2)' }}>Dates</span>
                        <input type="date" value={it.start_date ? String(it.start_date).slice(0,10) : ''} onChange={e => editStep(it, { start_date: e.target.value || null })} style={{ fontSize:11, padding:'2px 4px' }} />
                        <span style={{ color:'var(--text2)' }}>–</span>
                        <input type="date" value={it.end_date ? String(it.end_date).slice(0,10) : ''} onChange={e => editStep(it, { end_date: e.target.value || null })} style={{ fontSize:11, padding:'2px 4px' }} />
                        <select value={it.phase_status || ''} onChange={e => editStep(it, { phase_status: e.target.value || null })} style={{ fontSize:11, padding:'2px 4px' }}><option value="">— status —</option>{['Not Started','In Progress','On Hold','Completed'].map(s => <option key={s}>{s}</option>)}</select>
                        <select value={it.phase_lead_user_id || ''} onChange={e => editStep(it, { phase_lead_user_id: e.target.value || null })} style={{ fontSize:11, padding:'2px 4px' }}><option value="">— lead —</option>{d.members.map(m => <option key={m.user_id} value={m.user_id}>{m.full_name}</option>)}</select>
                        <button className="btn btn-secondary btn-sm" onClick={() => setAddFor(addFor === it.id ? null : it.id)}>+ Add task</button>
                      </>}
                    </div>
                    {addFor === it.id && <AddProjectTask item={it} members={d.members} onDone={() => { setAddFor(null); load(); }} setMsg={setMsg} />}
                  </td>
                </tr>
                {ts.length ? ts.map(t => (
                  <tr key={t.id}>
                    <td style={{ width:24 }}></td>
                    <td style={{ fontSize:12 }}>{canEditPlan
                      ? <input key={t.title} defaultValue={t.title} onBlur={e => { const v = e.target.value.trim(); if (v && v !== t.title) editTask(t, { title:v }); }} style={{ fontSize:12, padding:'2px 4px', width:'100%', minWidth:120 }} />
                      : t.title}</td>
                    <td style={{ fontSize:12 }}>{canEditPlan
                      ? <select value={t.assigned_to || ''} onChange={e => editTask(t, { assigned_to: e.target.value || null })} style={{ fontSize:12, padding:'2px 4px' }}><option value="">— unassigned —</option>{d.members.map(m => <option key={m.user_id} value={m.user_id}>{m.full_name}</option>)}{t.assigned_to && !d.members.some(m => m.user_id === t.assigned_to) && <option value={t.assigned_to}>{t.assigned_to_name}</option>}</select>
                      : (t.assigned_to_name || '—')}</td>
                    <td style={{ fontSize:12 }}>{canEditPlan
                      ? <input type="date" value={t.due_date ? String(t.due_date).slice(0,10) : ''} onChange={e => editTask(t, { due_date: e.target.value || null })} style={{ fontSize:11, padding:'1px 3px' }} />
                      : (t.due_date ? <span style={{ color: isOverdueT(t) ? 'var(--red)' : 'var(--text2)' }}>{dfmt(t.due_date)}</span> : '—')}</td>
                    <td>{canEditTask(t)
                      ? <select value={PLAN_STATUSES.includes(t.status) ? t.status : 'Pending'} onChange={e => setTaskStatus(t, e.target.value)} style={{ fontSize:12, padding:'2px 4px' }}>{PLAN_STATUSES.map(s => <option key={s}>{s}</option>)}</select>
                      : <StatusPill s={t.status} />}</td>
                    <td style={{ minWidth:80 }}><div className="flex-gap" style={{ gap:5, alignItems:'center' }}><div style={{ flex:1, height:6, background:'var(--bg3)', borderRadius:20, overflow:'hidden' }}><div style={{ width:taskPct(t)+'%', height:'100%', background: taskPct(t)===100?'var(--green)':'var(--accent)' }} /></div><span style={{ fontSize:10, color:'var(--text2)' }}>{taskPct(t)}%</span></div></td>
                    <td>{canEditPlan && <button className="btn btn-danger btn-sm" onClick={() => delT(t)}>✕</button>}</td>
                  </tr>
                )) : <tr><td style={{ width:24 }}></td><td colSpan={6} style={{ fontSize:12, color:'var(--text2)' }}>No tasks yet.</td></tr>}
              </React.Fragment>
            );
          })}
        </tbody>
      </table></div>
      {canEditPlan && (
        <div className="flex-gap" style={{ gap:8, padding:'10px 12px', borderTop:'1px solid var(--border)', flexWrap:'wrap', alignItems:'flex-end' }}>
          <div style={{ flex:1, minWidth:160 }}><div style={{ fontSize:11, color:'var(--text2)', marginBottom:2 }}>Phase / step</div><input value={newPhase.title} onChange={e => setNewPhase(s => ({ ...s, title:e.target.value }))} placeholder="New phase name" onKeyDown={e => { if (e.key === 'Enter') addPhase(); }} style={{ width:'100%', padding:'6px 8px' }} /></div>
          <div><div style={{ fontSize:11, color:'var(--text2)', marginBottom:2 }}>Start</div><input type="date" value={newPhase.start_date} onChange={e => setNewPhase(s => ({ ...s, start_date:e.target.value }))} style={{ padding:'6px 8px' }} /></div>
          <div><div style={{ fontSize:11, color:'var(--text2)', marginBottom:2 }}>End</div><input type="date" value={newPhase.end_date} onChange={e => setNewPhase(s => ({ ...s, end_date:e.target.value }))} style={{ padding:'6px 8px' }} /></div>
          <button className="btn btn-primary btn-sm" onClick={addPhase}>+ Add phase</button>
        </div>
      )}
    </div>
  );
}

function TimelineTab({ d, phaseInfo }) {
  const dates = [];
  phaseInfo.forEach(p => { if (p.start_date) dates.push(new Date(p.start_date)); if (p.end_date) dates.push(new Date(p.end_date)); });
  if (d.start_date) dates.push(new Date(d.start_date)); if (d.target_date) dates.push(new Date(d.target_date));
  if (!dates.length) return <div className="card" style={{ padding:16, color:'var(--text2)', fontSize:13 }}>Add start/end dates to phases (Phases &amp; Tasks tab) to see the timeline.</div>;
  const min = new Date(Math.min(...dates)), max = new Date(Math.max(...dates));
  const span = Math.max(1, max - min);
  const pos = dt => dt ? ((new Date(dt) - min) / span) * 100 : 0;
  const todayPos = pos(new Date());
  const f = x => x ? new Date(x).toLocaleDateString('en-IN', { day:'2-digit', month:'short' }) : '';
  return (
    <div className="card" style={{ padding:16 }}>
      <div style={{ fontSize:12, color:'var(--text2)', marginBottom:12 }}>{f(min)} → {f(max)}</div>
      <div style={{ position:'relative' }}>
        {phaseInfo.map(p => {
          const l = pos(p.start_date || p.end_date), r = pos(p.end_date || p.start_date); const w = Math.max(2, r - l);
          return (
            <div key={p.id} style={{ display:'flex', alignItems:'center', gap:10, marginBottom:8 }}>
              <div style={{ width:130, fontSize:12, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{p.num}. {p.title}</div>
              <div style={{ flex:1, position:'relative', height:18, background:'var(--bg3)', borderRadius:20 }}>
                {todayPos >= 0 && todayPos <= 100 && <div style={{ position:'absolute', left:todayPos+'%', top:-3, height:24, width:2, background:'var(--red)' }} title="Today" />}
                {(p.start_date || p.end_date) && <div title={`${f(p.start_date)} – ${f(p.end_date)}`} style={{ position:'absolute', left:l+'%', width:w+'%', top:0, height:18, borderRadius:20, background: p.pct===100 ? 'var(--green)' : p.behind ? 'var(--red)' : 'var(--accent)' }} />}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function TeamTab({ d, itUsers, wl, addMember, delMember, canEditPlan }) {
  return (<>
    <div className="card" style={{ marginBottom:14 }}>
      <div style={{ fontSize:12, fontWeight:600, color:'var(--text2)', marginBottom:8 }}>PROJECT MEMBERS</div>
      <div className="flex-gap" style={{ gap:8, flexWrap:'wrap', alignItems:'center' }}>
        {d.members.map(m => (
          <span key={m.user_id} className="flex-gap" style={{ gap:6, alignItems:'center', background:'var(--bg3)', borderRadius:20, padding:'3px 8px 3px 3px' }}>
            <Avatar name={m.full_name} size={22} /><span style={{ fontSize:12.5 }}>{m.full_name}</span>
            {canEditPlan && <button onClick={() => delMember(m.user_id)} style={{ background:'none', border:'none', cursor:'pointer', color:'var(--red)' }}>×</button>}
          </span>
        ))}
        {canEditPlan && <select defaultValue="" onChange={e => { addMember(e.target.value); e.target.value=''; }} style={{ padding:'4px 6px' }}><option value="">+ add member…</option>{itUsers.filter(u => !d.members.some(m => m.user_id === u.id)).map(u => <option key={u.id} value={u.id}>{u.full_name}</option>)}</select>}
      </div>
    </div>
    {wl.length > 0 && (
      <div className="card" style={{ padding:0, overflow:'hidden' }}>
        <div style={{ fontSize:12, fontWeight:600, color:'var(--text2)', padding:'10px 12px' }}>MEMBER WORKLOAD (this project)</div>
        <div className="table-wrap"><table><thead><tr><th>Member</th><th>Pending</th><th>Overdue</th><th>Due this week</th></tr></thead>
          <tbody>{wl.map(w => <tr key={w.id}><td><span className="flex-gap" style={{ gap:6, alignItems:'center' }}><Avatar name={w.full_name} size={20} />{w.full_name}</span></td><td>{w.pending}</td><td style={{ color: Number(w.overdue)>0?'var(--red)':'var(--text2)' }}>{w.overdue}</td><td style={{ color: Number(w.due_week)>0?'#FF9900':'var(--text2)' }}>{w.due_week}</td></tr>)}</tbody>
        </table></div>
      </div>
    )}
  </>);
}

function ActivityTab({ recent }) {
  const f = x => x ? new Date(x).toLocaleString('en-IN', { day:'2-digit', month:'short', hour:'2-digit', minute:'2-digit' }) : '';
  return (
    <div className="card" style={{ padding:0, overflow:'hidden' }}>
      <div style={{ fontSize:12, fontWeight:600, color:'var(--text2)', padding:'10px 12px' }}>RECENT ACTIVITY</div>
      <div style={{ padding:'0 14px 12px' }}>
        {recent.slice(0,15).map(t => (
          <div key={t.id} className="flex-gap" style={{ gap:10, alignItems:'center', padding:'7px 0', borderTop:'1px solid var(--border)' }}>
            <StatusPill s={t.status} />
            <span style={{ flex:1, fontSize:12.5 }}>{t.title} <span style={{ color:'var(--text2)' }}>· {t.phase}</span></span>
            <span style={{ fontSize:11, color:'var(--text2)' }}>{f(t.updated_at)}</span>
          </div>
        ))}
        {!recent.length && <div style={{ fontSize:12, color:'var(--text2)', paddingTop:8 }}>No activity yet.</div>}
      </div>
    </div>
  );
}

function EditProject({ id, d, itUsers, cats, onClose, onDone }) {
  const [f, setF] = React.useState({
    title:d.title||'', lifecycle:d.lifecycle||'Active', priority:d.priority||'', category:d.category||'',
    owner_user_id:d.owner_user_id||'', start_date:d.start_date?String(d.start_date).slice(0,10):'', target_date:d.target_date?String(d.target_date).slice(0,10):'',
    timeline:d.timeline||'', desired_outcome:d.desired_outcome||'', description:d.description||'',
    contributing_departments:(d.contributing_departments||[]).join(', '), health_flag:d.health_flag||'green', health_reason:d.health_reason||'' });
  const [busy, setBusy] = React.useState(false); const [err, setErr] = React.useState('');
  const save = async (force) => {
    setBusy(true); setErr('');
    try { await api.put(`/containers/${id}`, { ...f, owner_user_id:f.owner_user_id||null, start_date:f.start_date||null, target_date:f.target_date||null, force }); onDone(); }
    catch (e) { if (e.response && e.response.status===409 && e.response.data && e.response.data.needs_confirm) { setBusy(false); if (window.confirm(e.response.data.error)) return save(true); } else setErr((e.response && e.response.data && e.response.data.error)||'Save failed'); }
    finally { setBusy(false); }
  };
  return (
    <div className="modal-overlay" onClick={e => e.target===e.currentTarget && false && onClose()}>
      <div className="modal" style={{ maxWidth:640, maxHeight:'92vh', overflowY:'auto' }}>
        <div className="modal-header"><span className="modal-title">✎ Edit Project</span><button className="modal-close" onClick={onClose}>×</button></div>
        <div className="form-group"><label>Name *</label><input value={f.title} onChange={e => setF(s => ({ ...s, title:e.target.value }))} /></div>
        <div className="form-row">
          <div className="form-group"><label>Status</label><select value={f.lifecycle} onChange={e => setF(s => ({ ...s, lifecycle:e.target.value }))}>{['Draft','Planning','Active','On Hold','Completed','Cancelled'].map(x => <option key={x}>{x}</option>)}</select></div>
          <div className="form-group"><label>Priority</label><select value={f.priority} onChange={e => setF(s => ({ ...s, priority:e.target.value }))}><option value="">—</option>{['Low','Medium','High','Critical'].map(x => <option key={x}>{x}</option>)}</select></div>
          <div className="form-group"><label>Category</label><input list="ep-cats" value={f.category} onChange={e => setF(s => ({ ...s, category:e.target.value }))} /><datalist id="ep-cats">{cats.map(c => <option key={c} value={c} />)}</datalist></div>
        </div>
        <div className="form-row">
          <div className="form-group"><label>Project Lead</label><select value={f.owner_user_id} onChange={e => setF(s => ({ ...s, owner_user_id:e.target.value }))}><option value="">—</option>{itUsers.map(u => <option key={u.id} value={u.id}>{u.full_name}</option>)}</select></div>
          <div className="form-group"><label>Start date</label><input type="date" value={f.start_date} onChange={e => setF(s => ({ ...s, start_date:e.target.value }))} /></div>
          <div className="form-group"><label>Target date</label><input type="date" value={f.target_date} onChange={e => setF(s => ({ ...s, target_date:e.target.value }))} /></div>
        </div>
        <div className="form-group"><label>Contributing departments <span style={{ fontSize:11, color:'var(--text2)' }}>(comma-separated)</span></label><input value={f.contributing_departments} onChange={e => setF(s => ({ ...s, contributing_departments:e.target.value }))} placeholder="IT, Operations, Admin" /></div>
        <div className="form-group"><label>Timeline <span style={{ fontSize:11, color:'var(--text2)' }}>(free text)</span></label><input value={f.timeline} onChange={e => setF(s => ({ ...s, timeline:e.target.value }))} /></div>
        <div className="form-group"><label>Desired outcome</label><textarea rows={2} value={f.desired_outcome} onChange={e => setF(s => ({ ...s, desired_outcome:e.target.value }))} /></div>
        <div className="form-group"><label>Description</label><textarea rows={2} value={f.description} onChange={e => setF(s => ({ ...s, description:e.target.value }))} /></div>
        <div className="form-row">
          <div className="form-group"><label>Health</label><select value={f.health_flag} onChange={e => setF(s => ({ ...s, health_flag:e.target.value }))}>{['green','amber','red'].map(x => <option key={x} value={x}>{x[0].toUpperCase()+x.slice(1)}</option>)}</select></div>
          <div className="form-group" style={{ flex:2 }}><label>Health reason {f.health_flag!=='green' && '*'}</label><input value={f.health_reason} onChange={e => setF(s => ({ ...s, health_reason:e.target.value }))} placeholder="Required if not green" /></div>
        </div>
        {err && <div className="err-msg">{err}</div>}
        <div className="modal-footer"><button className="btn btn-secondary" onClick={onClose}>Cancel</button><button className="btn btn-primary" disabled={busy} onClick={() => save(false)}>{busy?'Saving…':'Save Changes'}</button></div>
      </div>
    </div>
  );
}

function AddTaskModal({ id, phases, members, onClose, onDone }) {
  const [f, setF] = React.useState({ phase: phases[0] ? phases[0].id : '', title:'', assigned_to:'', due_date:'', priority:'Normal' });
  const [busy, setBusy] = React.useState(false); const [err, setErr] = React.useState('');
  const go = async () => {
    if (!f.phase) { setErr('Pick a phase'); return; } if (!f.title.trim()) { setErr('Task title required'); return; } if (!f.assigned_to) { setErr('Pick a member'); return; }
    setBusy(true); setErr('');
    try { await api.post(`/containers/items/${f.phase}/task`, { title:f.title.trim(), assigned_to:f.assigned_to, due_date:f.due_date||null, priority:f.priority }); onDone(); }
    catch (e) { setErr((e.response && e.response.data && e.response.data.error)||'Failed'); } finally { setBusy(false); }
  };
  return (
    <div className="modal-overlay" onClick={e => e.target===e.currentTarget && false && onClose()}>
      <div className="modal" style={{ maxWidth:520 }}>
        <div className="modal-header"><span className="modal-title">+ Add Task</span><button className="modal-close" onClick={onClose}>×</button></div>
        {!members.length ? <div style={{ fontSize:13, color:'var(--text2)' }}>Add project members first (Team tab), then assign tasks.</div>
         : !phases.length ? <div style={{ fontSize:13, color:'var(--text2)' }}>Add a phase first (Phases &amp; Tasks tab).</div> : (<>
          <div className="form-group"><label>Phase</label><select value={f.phase} onChange={e => setF(s => ({ ...s, phase:e.target.value }))}>{phases.map(p => <option key={p.id} value={p.id}>{p.title}</option>)}</select></div>
          <div className="form-group"><label>Task *</label><input value={f.title} onChange={e => setF(s => ({ ...s, title:e.target.value }))} /></div>
          <div className="form-row">
            <div className="form-group"><label>Assign to *</label><select value={f.assigned_to} onChange={e => setF(s => ({ ...s, assigned_to:e.target.value }))}><option value="">— member —</option>{members.map(m => <option key={m.user_id} value={m.user_id}>{m.full_name}</option>)}</select></div>
            <div className="form-group"><label>Due date</label><input type="date" value={f.due_date} onChange={e => setF(s => ({ ...s, due_date:e.target.value }))} /></div>
            <div className="form-group"><label>Priority</label><select value={f.priority} onChange={e => setF(s => ({ ...s, priority:e.target.value }))}>{['Low','Normal','High'].map(p => <option key={p}>{p}</option>)}</select></div>
          </div>
          {err && <div className="err-msg">{err}</div>}
          <div className="modal-footer"><button className="btn btn-secondary" onClick={onClose}>Cancel</button><button className="btn btn-primary" disabled={busy} onClick={go}>{busy?'…':'Add Task'}</button></div>
        </>)}
      </div>
    </div>
  );
}
